// src/supabaseClient.js
// Initializes the Supabase connection for La Tavola

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = 'https://rxvvlsvayacjkqhgpoux.supabase.co';
const SUPABASE_KEY = 'sb_publishable_m4GflHDxK5JN_xfxa5Aa0w_pVimYuFd';

// Create a single Supabase client for the entire app
export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// Your restaurant's unique ID (matches what we inserted in SQL)
// MULTI-TENANT: Restaurant ID is dynamic - changes based on logged-in restaurant
// Default is your restaurant (auto-detected by slug 'la-tavola')
// In SaaS-2 we'll add proper login to switch restaurants
let _currentRestaurantId = null; // will be set after fetchRestaurantBySlug

// On module load, immediately try to read saved restaurant from localStorage
// This ensures _currentRestaurantId is set BEFORE any queries run
// EXCEPT if URL has ?r= param - then URL takes priority (customer browsing)
try {
  if (typeof localStorage !== 'undefined' && typeof window !== 'undefined') {
    // Check if URL is requesting a specific restaurant (customer link)
    const urlHasRestaurant = window.location.search.includes('r=') || window.location.search.includes('restaurant=');
    if (!urlHasRestaurant) {
      // Only use localStorage if not visiting via URL
      const raw = localStorage.getItem('latavola_saas_restaurant');
      if (raw) {
        const r = JSON.parse(raw);
        if (r && r.id) _currentRestaurantId = r.id;
      }
    }
  }
} catch (e) {
  // ignore - localStorage might not be available
}

export const setCurrentRestaurantId = (id) => {
  if (id) {
    _currentRestaurantId = id;
    RESTAURANT_ID = id; // also update legacy export
  }
};

export const getCurrentRestaurantId = () => _currentRestaurantId;

// Auto-detect "your" restaurant on app load
export async function autoDetectMyRestaurant() {
  try {
    const { data } = await supabase
      .from('restaurants')
      .select('id, name, slug, plan')
      .eq('slug', 'la-tavola')
      .single();
    if (data) {
      _currentRestaurantId = data.id;
      return data;
    }
  } catch (e) {
    console.log('Auto-detect failed:', e);
  }
  return null;
}

// RESTAURANT_ID: A let variable that updates when restaurant switches
// All queries should use _rid() to get current value dynamically
export let RESTAURANT_ID = _currentRestaurantId || '00000000-0000-0000-0000-000000000001';

// Helper that returns current restaurant ID (always fresh)
function _rid() {
  return _currentRestaurantId || RESTAURANT_ID;
}

// New: function that returns the current tenant's ID (preferred for new code)
export const TENANT_ID = () => _currentRestaurantId;

// Helper: generate a simple order number like ORD-12345
export const genOrderNumber = () => 'ORD-' + Math.floor(10000 + Math.random() * 90000);

// ---- ORDER HELPERS ----------------------------------------------------------

export async function saveOrderToDb(order) {
  const { data, error } = await supabase.from('orders').insert({
    order_number: order.id,
    restaurant_id: _rid(),
    branch_id: order.branchId || null,
    customer_name: order.customer,
    customer_phone: order.phone || null,
    items: order.items,
    subtotal: order.subtotal || order.total,
    delivery_fee: order.deliveryFee || 0,
    discount: order.discount || 0,
    tip: order.tip || 0,
    total: order.total,
    status: order.status || 'pending',
    type: order.type || 'dine-in',
    paid: order.paid || false,
    pay_method: order.payMethod || null,
    address: order.address || null,
    slot: order.slot || null,
    taken_by: order.takenBy || null,
    source: order.source || 'online',
    notes: order.notes || null,
    table_id: order.tableId || null,
    delivery_code: order.deliveryCode || null,
    code_method: order.codeMethod || 'app',
    service_charge: parseFloat(order.serviceCharge) || 0,
  }).select().single();
  if (error) console.error('saveOrderToDb error:', error);
  return { data, error };
}

export async function fetchOrders() {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .eq('restaurant_id', _rid())
    .order('created_at', { ascending: false })
    .limit(1000);
  if (error) console.error('fetchOrders error:', error);
  return data || [];
}

export async function updateOrderStatus(orderId, newStatus) {
  // Try by id (UUID) first
  let result = await supabase
    .from('orders')
    .update({ status: newStatus })
    .eq('id', orderId)
    .select();
  
  // If no rows matched, try by order_number
  if (!result.data || result.data.length === 0) {
    result = await supabase
      .from('orders')
      .update({ status: newStatus })
      .eq('order_number', orderId)
      .select();
  }
  
  if (result.error) console.error('updateOrderStatus error:', result.error);
  
  // AUTO COMMISSION HANDLING
  // When order is delivered/collected + paid → record commission
  // When order is cancelled → reverse any existing commission
  try {
    const order = result.data && result.data[0];
    if (order && order.restaurant_id) {
      if ((newStatus === 'delivered' || newStatus === 'collected') && order.paid) {
        await maybeRecordCommissionForOrder(order);
      } else if (newStatus === 'cancelled') {
        await reverseCommission(order.id, order.order_number);
      }
    }
  } catch (e) { console.log('Commission auto-handle skipped:', e); }
  
  return result;
}

export async function updateOrderPayment(orderId, paid, payMethod) {
  // Try by id (UUID) first
  let result = await supabase
    .from('orders')
    .update({ paid: paid, pay_method: payMethod })
    .eq('id', orderId)
    .select();
  
  // If no rows matched, try by order_number
  if (!result.data || result.data.length === 0) {
    result = await supabase
      .from('orders')
      .update({ paid: paid, pay_method: payMethod })
      .eq('order_number', orderId)
      .select();
  }
  
  if (result.error) console.error('updateOrderPayment error:', result.error);
  
  // AUTO COMMISSION HANDLING
  // If payment is now true AND status is already delivered/collected → record commission
  try {
    const order = result.data && result.data[0];
    if (order && order.restaurant_id && paid === true) {
      if (order.status === 'delivered' || order.status === 'collected') {
        await maybeRecordCommissionForOrder(order);
      }
    }
  } catch (e) { console.log('Commission auto-handle (payment) skipped:', e); }
  
  return result;
}

// Internal helper - look up restaurant and record commission if eligible
async function maybeRecordCommissionForOrder(order) {
  // Skip non-online orders
  const src = order.source;
  if (src === 'phone' || src === 'pos' || src === 'walk-in' || src === 'staff') return;
  
  // Get restaurant
  const { data: rest } = await supabase
    .from('restaurants')
    .select('id, name, billing_type, commission_rate')
    .eq('id', order.restaurant_id)
    .single();
  
  if (!rest || rest.billing_type !== 'commission') return;
  
  // Use the public recordCommission (already idempotent)
  const orderObj = {
    dbId: order.id,
    id: order.order_number,
    total: order.total,
    source: order.source,
  };
  await recordCommission(orderObj, rest);
}

// ---- CUSTOMER HELPERS -------------------------------------------------------

export async function findCustomerByPhone(phone) {
  const clean = phone.replace(/\s+/g, '');
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('restaurant_id', _rid())
    .eq('phone', clean)
    .maybeSingle();
  if (error) console.error('findCustomerByPhone error:', error);
  return data;
}

export async function fetchCustomers() {
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('restaurant_id', _rid())
    .order('created_at', { ascending: false });
  if (error) console.error('fetchCustomers error:', error);
  return data || [];
}

export async function saveCustomer(customer) {
  const { data, error } = await supabase.from('customers').insert({
    restaurant_id: _rid(),
    phone: customer.phone,
    name: customer.name,
    email: customer.email || null,
    address: customer.address || {},
    distance: customer.distance || null,
    notes: customer.notes || null,
  }).select().single();
  if (error) console.error('saveCustomer error:', error);
  return { data, error };
}

export async function updateCustomerStats(customerId, orderTotal) {
  const { data: existing } = await supabase
    .from('customers')
    .select('total_orders, total_spent')
    .eq('id', customerId)
    .single();
  if (!existing) return;
  await supabase.from('customers').update({
    total_orders: (existing.total_orders || 0) + 1,
    total_spent: (parseFloat(existing.total_spent) || 0) + orderTotal,
  }).eq('id', customerId);
}

// ---- MENU HELPERS -----------------------------------------------------------

export async function fetchMenu() {
  const { data, error } = await supabase
    .from('menu_items')
    .select('*')
    .eq('restaurant_id', _rid());
  if (error) console.error('fetchMenu error:', error);
  return data || [];
}

export async function saveMenuItem(item) {
  // Save menu item with category name directly
  const payload = {
    restaurant_id: _rid(),
    name: item.name,
    description: item.desc || null,
    price: parseFloat(item.price),
    icon: item.icon || 'cart',
    stock: parseInt(item.stock) || 20,
    available: item.avail !== false,
    allergens: item.allergens || [],
    sizes: item.sizes || [],
    extras: item.extras || [],
    cooking_opts: item.cookingOpts || [],
    category_name: item.cat || null,
    station: item.station || null,
    price_dinein: item.priceDineIn || null,
    price_takeaway: item.priceTakeaway || null,
    price_delivery: item.priceDelivery || null,
    avail_dinein: item.availDineIn !== false,
    avail_takeaway: item.availTakeaway !== false,
    avail_delivery: item.availDelivery !== false,
    image_url: item.image_url || null,
  };
  
  // If item has a UUID id (from database), update. Otherwise insert.
  if (item.dbId) {
    const { data, error } = await supabase.from('menu_items')
      .update(payload)
      .eq('id', item.dbId)
      .select().single();
    if (error) console.error('updateMenuItem error:', error);
    return { data, error };
  } else {
    const { data, error } = await supabase.from('menu_items')
      .insert(payload)
      .select().single();
    if (error) console.error('insertMenuItem error:', error);
    return { data, error };
  }
}

export async function deleteMenuItem(dbId) {
  const { error } = await supabase.from('menu_items').delete().eq('id', dbId);
  if (error) console.error('deleteMenuItem error:', error);
  return { error };
}

// Update specific fields on a menu item (e.g. just availability)
export async function updateMenuItem(dbId, updates) {
  const { data, error } = await supabase.from('menu_items')
    .update(updates)
    .eq('id', dbId)
    .select()
    .single();
  if (error) console.error('updateMenuItem error:', error);
  return { data, error };
}

// ---- CATEGORY HELPERS -------------------------------------------------------

export async function fetchCategories() {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .eq('restaurant_id', _rid())
    .order('display_order', { ascending: true });
  if (error) console.error('fetchCategories error:', error);
  return data || [];
}

export async function saveCategory(cat) {
  const payload = {
    restaurant_id: _rid(),
    name: cat.name,
    icon: cat.icon || 'star',
    display_order: parseInt(cat.order) || 99,
  };
  
  if (cat.dbId) {
    const { data, error } = await supabase.from('categories')
      .update(payload)
      .eq('id', cat.dbId)
      .select().single();
    if (error) console.error('updateCategory error:', error);
    return { data, error };
  } else {
    const { data, error } = await supabase.from('categories')
      .insert(payload)
      .select().single();
    if (error) console.error('insertCategory error:', error);
    return { data, error };
  }
}

export async function deleteCategory(dbId) {
  const { error } = await supabase.from('categories').delete().eq('id', dbId);
  if (error) console.error('deleteCategory error:', error);
  return { error };
}

// ---- SET MEAL HELPERS -------------------------------------------------------

export async function fetchSetMeals() {
  const { data, error } = await supabase
    .from('set_meals')
    .select('*')
    .eq('restaurant_id', _rid());
  if (error) console.error('fetchSetMeals error:', error);
  return data || [];
}

export async function saveSetMeal(meal) {
  const payload = {
    restaurant_id: _rid(),
    name: meal.name,
    description: meal.desc || null,
    price: parseFloat(meal.price),
    icon: meal.icon || 'party',
    item_ids: meal.itemIds || [],
    available: meal.avail !== false,
  };
  
  if (meal.dbId) {
    const { data, error } = await supabase.from('set_meals')
      .update(payload)
      .eq('id', meal.dbId)
      .select().single();
    if (error) console.error('updateSetMeal error:', error);
    return { data, error };
  } else {
    const { data, error } = await supabase.from('set_meals')
      .insert(payload)
      .select().single();
    if (error) console.error('insertSetMeal error:', error);
    return { data, error };
  }
}

export async function deleteSetMeal(dbId) {
  const { error } = await supabase.from('set_meals').delete().eq('id', dbId);
  if (error) console.error('deleteSetMeal error:', error);
  return { error };
}

// ---- REVIEW HELPERS ---------------------------------------------------------

export async function fetchReviews() {
  const { data, error } = await supabase
    .from('reviews')
    .select('*')
    .eq('restaurant_id', _rid())
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) console.error('fetchReviews error:', error);
  return data || [];
}

export async function submitReview(review) {
  const { error } = await supabase.from('reviews').insert({
    restaurant_id: _rid(),
    customer_name: review.customer,
    rating: review.rating,
    comment: review.comment,
  });
  if (error) console.error('submitReview error:', error);
}

// ---- REALTIME SUBSCRIPTIONS -------------------------------------------------

export function subscribeToOrders(onChange) {
  const channel = supabase
    .channel('orders-realtime')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'orders' },
      (payload) => onChange(payload)
    )
    .subscribe();
  return () => supabase.removeChannel(channel);
}

// ---- OPENING HOURS ----------------------------------------------------------
export async function fetchOpeningHours(branchId) {
  const { data, error } = await supabase.from('opening_hours')
    .select('*')
    .eq('restaurant_id', _rid())
    .eq('branch_id', branchId)
    .order('day_of_week');
  if (error) console.error('fetchOpeningHours:', error);
  return data || [];
}

export async function saveOpeningHours(branchId, dayOfWeek, openTime, closeTime, isClosed) {
  // Upsert: delete old then insert new (simpler than upsert for our case)
  await supabase.from('opening_hours')
    .delete()
    .eq('restaurant_id', _rid())
    .eq('branch_id', branchId)
    .eq('day_of_week', dayOfWeek);
  
  const { data, error } = await supabase.from('opening_hours').insert({
    restaurant_id: _rid(),
    branch_id: branchId,
    day_of_week: dayOfWeek,
    open_time: isClosed ? null : openTime,
    close_time: isClosed ? null : closeTime,
    is_closed: isClosed,
  }).select().single();
  if (error) console.error('saveOpeningHours:', error);
  return { data, error };
}

// ---- RESERVATIONS ---------------------------------------------------------
export async function saveReservation(res) {
  const { data, error } = await supabase.from('reservations').insert({
    restaurant_id: _rid(),
    branch_id: res.branchId,
    customer_name: res.name,
    customer_email: res.email,
    customer_phone: res.phone || null,
    party_size: parseInt(res.guests) || 2,
    reservation_date: res.date,
    reservation_time: res.time,
    notes: res.notes || null,
    status: res.status || 'confirmed',
    table_id: res.tableId || null,
  }).select().single();
  if (error) console.error('saveReservation:', error);
  return { data, error };
}

export async function fetchReservations(branchId, dateFrom, dateTo) {
  let q = supabase.from('reservations').select('*').eq('restaurant_id', _rid());
  if (branchId) q = q.eq('branch_id', branchId);
  if (dateFrom) q = q.gte('reservation_date', dateFrom);
  if (dateTo) q = q.lte('reservation_date', dateTo);
  const { data, error } = await q.order('reservation_date').order('reservation_time');
  if (error) console.error('fetchReservations:', error);
  return data || [];
}

export async function updateReservationStatus(id, status) {
  const { data, error } = await supabase.from('reservations')
    .update({ status })
    .eq('id', id)
    .select().single();
  return { data, error };
}

// ---- TABLES ---------------------------------------------------------------
export async function fetchTables(branchId) {
  let q = supabase.from('restaurant_tables').select('*').eq('restaurant_id', _rid());
  if (branchId) q = q.eq('branch_id', branchId);
  const { data, error } = await q.order('table_number');
  if (error) console.error('fetchTables:', error);
  return data || [];
}

export async function updateTableStatus(id, status, extra) {
  const payload = { status, ...(extra || {}) };
  const { data, error } = await supabase.from('restaurant_tables')
    .update(payload)
    .eq('id', id)
    .select().single();
  if (error) console.error('updateTableStatus:', error);
  return { data, error };
}

export async function saveTable(table) {
  if (table.dbId) {
    const { data, error } = await supabase.from('restaurant_tables')
      .update({
        seats: table.seats,
        x_pos: table.x,
        y_pos: table.y,
        position_x: table.x,
        position_y: table.y,
        status: table.status,
        table_number: table.id,
        shape: table.shape || 'rectangle',
      })
      .eq('id', table.dbId)
      .select().single();
    return { data, error };
  } else {
    const { data, error } = await supabase.from('restaurant_tables').insert({
      restaurant_id: _rid(),
      branch_id: table.branchId || 'b1',
      table_number: table.id,
      seats: table.seats,
      x_pos: table.x,
      y_pos: table.y,
      position_x: table.x,
      position_y: table.y,
      status: table.status || 'free',
      shape: table.shape || 'rectangle',
    }).select().single();
    return { data, error };
  }
}

export async function deleteTable(dbId) {
  const { error } = await supabase.from('restaurant_tables')
    .delete()
    .eq('id', dbId);
  if (error) console.error('deleteTable:', error);
  return { error };
}

// ---- CUSTOMER AUTH ----------------------------------------------------------
// Simple email+password auth using customers table
// Note: For production, use Supabase Auth (supabase.auth.signUp) for real security

export async function registerCustomer(name, email, password, phone) {
  // Check if email already exists
  const { data: existing } = await supabase.from('customers')
    .select('id')
    .eq('email', email)
    .eq('restaurant_id', _rid())
    .maybeSingle();
  if (existing) {
    return { error: { message: 'Email already registered' } };
  }
  // Insert new customer with password (stored as-is for demo; use Supabase Auth in production)
  const { data, error } = await supabase.from('customers').insert({
    restaurant_id: _rid(),
    name: name,
    email: email,
    phone: phone || null,
    password_hash: password, // In production, hash this or use Supabase Auth
    address: {},
  }).select().single();
  if (error) console.error('registerCustomer:', error);
  return { data, error };
}

export async function loginCustomer(email, password) {
  const { data, error } = await supabase.from('customers')
    .select('*')
    .eq('email', email)
    .eq('restaurant_id', _rid())
    .maybeSingle();
  if (error) {
    console.error('loginCustomer:', error);
    return { error };
  }
  if (!data) return { error: { message: 'Email not found' } };
  if (data.password_hash !== password) return { error: { message: 'Incorrect password' } };
  return { data };
}

// ---- DELIVERY SETTINGS ------------------------------------------------------
export async function fetchDeliverySettings(branchId) {
  let q = supabase.from('delivery_settings').select('*').eq('restaurant_id', _rid());
  if (branchId) q = q.eq('branch_id', branchId).maybeSingle();
  const { data, error } = await q;
  if (error) console.error('fetchDeliverySettings:', error);
  return data;
}

export async function fetchAllDeliverySettings() {
  const { data, error } = await supabase.from('delivery_settings')
    .select('*').eq('restaurant_id', _rid());
  if (error) console.error('fetchAllDeliverySettings:', error);
  return data || [];
}

export async function saveDeliverySettings(branchId, settings) {
  // Check if exists
  const { data: existing } = await supabase.from('delivery_settings')
    .select('id').eq('restaurant_id', _rid()).eq('branch_id', branchId).maybeSingle();
  
  const payload = {
    restaurant_id: _rid(),
    branch_id: branchId,
    method: settings.method || 'radius',
    enabled: settings.enabled !== false,
    min_order: settings.minOrder || 0,
    free_over: settings.freeOver || 0,
    flat_fee: settings.flatFee || 0,
    max_radius: settings.maxRadius || 3,
    zones: settings.zones || [],
    postcodes: settings.postcodes || [],
    cod_enabled: settings.codEnabled !== false,
    cod_min_order: settings.codMinOrder || 15,
    cod_max_miles: settings.codMaxMiles || 3,
    service_charge_enabled: settings.serviceChargeEnabled || false,
    service_charge_percent: parseFloat(settings.serviceChargePercent) || 12.5,
    service_charge_mandatory: settings.serviceChargeMandatory || false,
    service_charge_group_size: parseInt(settings.serviceChargeGroupSize) || 6,
    updated_at: new Date().toISOString(),
  };
  
  if (existing) {
    const { data, error } = await supabase.from('delivery_settings')
      .update(payload).eq('id', existing.id).select().single();
    if (error) console.error('updateDeliverySettings:', error);
    return { data, error };
  } else {
    const { data, error } = await supabase.from('delivery_settings')
      .insert(payload).select().single();
    if (error) console.error('insertDeliverySettings:', error);
    return { data, error };
  }
}

// ---- DISCOUNT CODES ---------------------------------------------------------
export async function fetchDiscountCodes() {
  const { data, error } = await supabase.from('discount_codes')
    .select('*').eq('restaurant_id', _rid()).order('created_at', { ascending: false });
  if (error) console.error('fetchDiscountCodes:', error);
  return data || [];
}

export async function saveDiscountCode(code) {
  const payload = {
    restaurant_id: _rid(),
    code: code.code.toUpperCase().trim(),
    type: code.type || 'percent',
    value: parseFloat(code.value) || 0,
    description: code.description || null,
    min_order: parseFloat(code.minOrder) || 0,
    max_uses: parseInt(code.maxUses) || 1000,
    uses: code.uses || 0,
    expires_at: code.expiresAt || null,
    active: code.active !== false,
    first_order_only: code.firstOrderOnly || false,
    branch_ids: code.branchIds || [],
  };
  if (code.dbId) {
    const { data, error } = await supabase.from('discount_codes')
      .update(payload).eq('id', code.dbId).select().single();
    return { data, error };
  } else {
    const { data, error } = await supabase.from('discount_codes')
      .insert(payload).select().single();
    return { data, error };
  }
}

export async function deleteDiscountCode(dbId) {
  const { error } = await supabase.from('discount_codes').delete().eq('id', dbId);
  return { error };
}

export async function incrementDiscountUse(code) {
  const { data } = await supabase.from('discount_codes')
    .select('uses').eq('restaurant_id', _rid()).eq('code', code.toUpperCase()).maybeSingle();
  if (data) {
    await supabase.from('discount_codes')
      .update({ uses: (data.uses || 0) + 1 })
      .eq('restaurant_id', _rid()).eq('code', code.toUpperCase());
  }
}

// ---- AUTO DISCOUNTS ---------------------------------------------------------
export async function fetchAutoDiscounts() {
  const { data, error } = await supabase.from('auto_discounts')
    .select('*').eq('restaurant_id', _rid()).order('created_at', { ascending: false });
  if (error) console.error('fetchAutoDiscounts:', error);
  return data || [];
}

export async function saveAutoDiscount(ad) {
  const payload = {
    restaurant_id: _rid(),
    name: ad.name,
    description: ad.description || null,
    rule_type: ad.ruleType || 'min_order',
    min_order: parseFloat(ad.minOrder) || 0,
    discount_type: ad.discountType || 'percent',
    discount_value: parseFloat(ad.discountValue) || 10,
    active: ad.active !== false,
    first_order_only: ad.firstOrderOnly || false,
    branch_ids: ad.branchIds || [],
  };
  if (ad.dbId) {
    const { data, error } = await supabase.from('auto_discounts')
      .update(payload).eq('id', ad.dbId).select().single();
    return { data, error };
  } else {
    const { data, error } = await supabase.from('auto_discounts')
      .insert(payload).select().single();
    return { data, error };
  }
}

export async function deleteAutoDiscount(dbId) {
  const { error } = await supabase.from('auto_discounts').delete().eq('id', dbId);
  return { error };
}

// ---- KITCHEN STATIONS -------------------------------------------------------
export async function fetchStations() {
  const { data, error } = await supabase.from('kitchen_stations')
    .select('*').eq('restaurant_id', _rid())
    .order('sort_order', { ascending: true });
  if (error) console.error('fetchStations:', error);
  return data || [];
}

export async function saveStation(s) {
  const payload = {
    restaurant_id: _rid(),
    name: s.name,
    icon: s.icon || 'cook',
    color: s.color || '#bf4626',
    sort_order: parseInt(s.sortOrder) || 0,
    active: s.active !== false,
    printer_method: s.printerMethod || 'none',
    printer_format: s.printerFormat || 'thermal',
    print_content: s.printContent || 'station_only',
    auto_print: s.autoPrint || false,
    printnode_id: s.printnodeId || null,
    copies: parseInt(s.copies) || 1,
  };
  if (s.dbId) {
    const { data, error } = await supabase.from('kitchen_stations')
      .update(payload).eq('id', s.dbId).select().single();
    return { data, error };
  } else {
    const { data, error } = await supabase.from('kitchen_stations')
      .insert(payload).select().single();
    return { data, error };
  }
}

export async function deleteStation(dbId) {
  const { error } = await supabase.from('kitchen_stations').delete().eq('id', dbId);
  return { error };
}

export async function updateStationProgress(orderId, stationName, isReady) {
  // Get current order
  const { data: order } = await supabase.from('orders')
    .select('station_progress')
    .eq('order_number', orderId).maybeSingle();
  const progress = (order && order.station_progress) || {};
  progress[stationName] = isReady;
  const { error } = await supabase.from('orders')
    .update({ station_progress: progress })
    .eq('order_number', orderId);
  return { error, progress };
}

// ---- DELIVERY CODES + CASH RECONCILIATION -----------------------------------
export async function verifyDeliveryCode(orderId, code, driverName) {
  const { data: order } = await supabase.from('orders')
    .select('delivery_code,total,pay_method,paid')
    .eq('order_number', orderId).maybeSingle();
  if (!order) return { ok: false, reason: 'Order not found' };
  if (!order.delivery_code) return { ok: false, reason: 'No code set on this order' };
  if (String(order.delivery_code) !== String(code)) return { ok: false, reason: 'Code does not match' };
  // Mark as delivered
  await supabase.from('orders').update({
    status: 'delivered',
    delivered_at: new Date().toISOString(),
    delivered_by: driverName,
  }).eq('order_number', orderId);
  return { ok: true, isCOD: order.pay_method === 'cod' && !order.paid, total: parseFloat(order.total) };
}

export async function recordCashCollected(orderId, amount, driverName) {
  const { error } = await supabase.from('orders').update({
    paid: true,
    pay_method: 'cash',
    cash_collected: amount,
    delivered_by: driverName,
  }).eq('order_number', orderId);
  return { error };
}

export async function fetchCashHandovers(branchId) {
  let q = supabase.from('cash_handovers').select('*').eq('restaurant_id', _rid());
  const { data, error } = await q.order('initiated_at', { ascending: false });
  if (error) console.error('fetchCashHandovers:', error);
  return data || [];
}

export async function recordCashHandover(handover) {
  // Uses the cash_handovers table with the new schema column names.
  // This is the "manager directly confirms" path (no driver-initiated step).
  const declared = parseFloat(handover.expectedAmount || handover.amount || 0);
  const confirmed = parseFloat(handover.amount || 0);
  const { data, error } = await supabase.from('cash_handovers').insert({
    restaurant_id: _rid(),
    driver_name: handover.driverName,
    manager_name: handover.managerName,
    driver_declared_amount: declared,
    manager_confirmed_amount: confirmed,
    discrepancy: confirmed - declared,
    order_ids: handover.orderIds || [],
    order_count: (handover.orderIds || []).length,
    status: 'confirmed',
    confirmed_at: new Date().toISOString(),
    note: handover.notes || null,
  }).select().single();
  // Mark associated orders with handover id
  if (data && handover.orderIds && handover.orderIds.length) {
    // orderIds here may be UUIDs or order_numbers - try both
    await supabase.from('orders').update({ cash_handover_id: data.id, handover_id: data.id })
      .in('id', handover.orderIds);
    await supabase.from('orders').update({ cash_handover_id: data.id, handover_id: data.id })
      .in('order_number', handover.orderIds);
  }
  return { data, error };
}

// ---- LOYALTY POINTS ---------------------------------------------------------
export async function fetchCustomerLoyalty(customerId) {
  const { data, error } = await supabase.from('customers')
    .select('loyalty_points,total_spent,loyalty_tier')
    .eq('id', customerId).maybeSingle();
  if (error) console.error('fetchCustomerLoyalty:', error);
  return data || { loyalty_points: 0, total_spent: 0, loyalty_tier: 'bronze' };
}

export async function awardLoyaltyPoints(customerId, points, orderId, description) {
  // Get current points
  const { data: cust } = await supabase.from('customers')
    .select('loyalty_points,total_spent').eq('id', customerId).maybeSingle();
  if (!cust) return { error: 'Customer not found' };
  const newPoints = (cust.loyalty_points || 0) + points;
  // Determine tier
  let tier = 'bronze';
  if (newPoints >= 2000) tier = 'gold';
  else if (newPoints >= 500) tier = 'silver';
  // Update customer
  await supabase.from('customers').update({
    loyalty_points: newPoints,
    loyalty_tier: tier,
  }).eq('id', customerId);
  // Log transaction
  await supabase.from('loyalty_transactions').insert({
    restaurant_id: _rid(),
    customer_id: customerId,
    type: 'earn',
    points: points,
    order_id: orderId,
    description: description || 'Order rewards',
  });
  return { points: newPoints, tier };
}

export async function redeemLoyaltyPoints(customerId, points, orderId, description) {
  const { data: cust } = await supabase.from('customers')
    .select('loyalty_points').eq('id', customerId).maybeSingle();
  if (!cust) return { error: 'Customer not found' };
  if ((cust.loyalty_points || 0) < points) return { error: 'Not enough points' };
  const newPoints = cust.loyalty_points - points;
  await supabase.from('customers').update({ loyalty_points: newPoints }).eq('id', customerId);
  await supabase.from('loyalty_transactions').insert({
    restaurant_id: _rid(),
    customer_id: customerId,
    type: 'redeem',
    points: -points,
    order_id: orderId,
    description: description || 'Discount redeemed',
  });
  return { points: newPoints };
}

export async function fetchLoyaltyHistory(customerId) {
  const { data, error } = await supabase.from('loyalty_transactions')
    .select('*').eq('customer_id', customerId)
    .order('created_at', { ascending: false }).limit(20);
  if (error) console.error('fetchLoyaltyHistory:', error);
  return data || [];
}

// ---- DIETARY PREFERENCES ----------------------------------------------------
export async function fetchDietaryPrefs(customerId) {
  const { data } = await supabase.from('customers')
    .select('dietary_prefs').eq('id', customerId).maybeSingle();
  return data?.dietary_prefs || [];
}

export async function saveDietaryPrefs(customerId, prefs) {
  const { error } = await supabase.from('customers').update({
    dietary_prefs: prefs || [],
  }).eq('id', customerId);
  return { error };
}

// ---- STAFF SCHEDULING -------------------------------------------------------
export async function fetchSchedules(branchId, fromDate, toDate) {
  let q = supabase.from('employee_schedules').select('*').eq('restaurant_id', _rid());
  if (branchId) q = q.eq('branch_id', branchId);
  if (fromDate) q = q.gte('shift_date', fromDate);
  if (toDate) q = q.lte('shift_date', toDate);
  const { data, error } = await q.order('shift_date').order('shift_start');
  if (error) console.error('fetchSchedules:', error);
  return data || [];
}

export async function saveSchedule(schedule) {
  const payload = {
    restaurant_id: _rid(),
    branch_id: schedule.branchId || null,
    staff_id: schedule.staffId,
    staff_name: schedule.staffName,
    staff_role: schedule.staffRole || 'staff',
    shift_date: schedule.shiftDate,
    shift_start: schedule.shiftStart,
    shift_end: schedule.shiftEnd,
    notes: schedule.notes || null,
  };
  if (schedule.id) {
    const { error } = await supabase.from('employee_schedules').update(payload).eq('id', schedule.id);
    return { error };
  }
  const { data, error } = await supabase.from('employee_schedules').insert(payload).select().single();
  return { data, error };
}

export async function deleteSchedule(id) {
  const { error } = await supabase.from('employee_schedules').delete().eq('id', id);
  return { error };
}

// ---- STAFF CLOCK IN/OUT -----------------------------------------------------
export async function clockIn(staffId, staffName, branchId) {
  // Check if already clocked in
  const { data: open } = await supabase.from('employee_time_clock')
    .select('*').eq('staff_id', staffId).is('clock_out', null).maybeSingle();
  if (open) return { error: 'Already clocked in', record: open };
  const { data, error } = await supabase.from('employee_time_clock').insert({
    restaurant_id: _rid(),
    branch_id: branchId || null,
    staff_id: staffId,
    staff_name: staffName,
  }).select().single();
  return { data, error };
}

export async function clockOut(staffId) {
  const { data: open } = await supabase.from('employee_time_clock')
    .select('*').eq('staff_id', staffId).is('clock_out', null)
    .order('clock_in', { ascending: false }).limit(1).maybeSingle();
  if (!open) return { error: 'Not clocked in' };
  const clockOutTime = new Date();
  const minutes = Math.round((clockOutTime - new Date(open.clock_in)) / 60000);
  const { error } = await supabase.from('employee_time_clock').update({
    clock_out: clockOutTime.toISOString(),
    total_minutes: minutes,
  }).eq('id', open.id);
  return { error, minutes };
}

export async function fetchClockRecords(staffId, fromDate) {
  let q = supabase.from('employee_time_clock').select('*').eq('restaurant_id', _rid());
  if (staffId) q = q.eq('staff_id', staffId);
  if (fromDate) q = q.gte('clock_in', fromDate);
  const { data, error } = await q.order('clock_in', { ascending: false }).limit(50);
  if (error) console.error('fetchClockRecords:', error);
  return data || [];
}

export async function fetchCurrentlyClockedIn(branchId) {
  let q = supabase.from('employee_time_clock').select('*').is('clock_out', null);
  if (branchId) q = q.eq('branch_id', branchId);
  const { data } = await q;
  return data || [];
}

// ===========================================================
// ADVANCED OPENING HOURS
// ===========================================================

export async function fetchBranchHours(branchId) {
  let q = supabase.from('branch_hours').select('*').order('day_of_week');
  if (branchId) q = q.eq('branch_id', branchId);
  const { data, error } = await q;
  if (error) console.error('fetchBranchHours:', error);
  return data || [];
}

export async function saveBranchHours(branchId, dayOfWeek, serviceType, serviceWindow, hours) {
  // hours = { is_closed, open_time, close_time, last_order_time }
  const { data, error } = await supabase
    .from('branch_hours')
    .upsert({
      branch_id: branchId,
      day_of_week: dayOfWeek,
      service_type: serviceType || 'all',
      service_window: serviceWindow || 1,
      is_closed: hours.is_closed || false,
      open_time: hours.open_time || null,
      close_time: hours.close_time || null,
      last_order_time: hours.last_order_time || null,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'branch_id,day_of_week,service_type,service_window' })
    .select();
  if (error) console.error('saveBranchHours:', error);
  return { data: data?.[0], error };
}

export async function deleteBranchHours(id) {
  const { error } = await supabase.from('branch_hours').delete().eq('id', id);
  if (error) console.error('deleteBranchHours:', error);
}

export async function fetchBranchHolidays(branchId) {
  let q = supabase.from('branch_holidays').select('*').order('holiday_date');
  if (branchId) q = q.eq('branch_id', branchId);
  const { data, error } = await q;
  if (error) console.error('fetchBranchHolidays:', error);
  return data || [];
}

export async function saveBranchHoliday(holiday) {
  const { data, error } = await supabase
    .from('branch_holidays')
    .upsert(holiday, { onConflict: 'branch_id,holiday_date' })
    .select();
  if (error) console.error('saveBranchHoliday:', error);
  return { data: data?.[0], error };
}

export async function deleteBranchHoliday(id) {
  const { error } = await supabase.from('branch_holidays').delete().eq('id', id);
  if (error) console.error('deleteBranchHoliday:', error);
}

export async function fetchBranchHoursConfig(branchId) {
  const { data, error } = await supabase
    .from('branch_hours_config')
    .select('*')
    .eq('branch_id', branchId)
    .maybeSingle();
  if (error) console.error('fetchBranchHoursConfig:', error);
  return data;
}

export async function saveBranchHoursConfig(config) {
  const { data, error } = await supabase
    .from('branch_hours_config')
    .upsert({ ...config, updated_at: new Date().toISOString() }, { onConflict: 'branch_id' })
    .select();
  if (error) console.error('saveBranchHoursConfig:', error);
  return { data: data?.[0], error };
}

// ===========================================================
// CASH MANAGEMENT, SHIFTS, VOIDS
// ===========================================================

export async function recordPayment(payment) {
  // payment = { order_id, branch_id, payment_method, amount, cash_given, change_returned, tip_amount, taken_by, shift_id }
  const { data, error } = await supabase.from('order_payments').insert(payment).select();
  if (error) console.error('recordPayment:', error);
  return { data: data?.[0], error };
}

export async function fetchOrderPayments(orderId) {
  const { data } = await supabase.from('order_payments').select('*').eq('order_id', orderId);
  return data || [];
}

// SHIFTS
export async function openShift(branchId, staffName, openingFloat) {
  const { data, error } = await supabase
    .from('staff_shifts')
    .insert({ branch_id: branchId, staff_name: staffName, opening_float: openingFloat || 0, status: 'open' })
    .select();
  if (error) console.error('openShift:', error);
  return { data: data?.[0], error };
}

export async function closeShift(shiftId, actualCash, notes) {
  // Fetch shift to compute variance
  const { data: shift } = await supabase.from('staff_shifts').select('*').eq('id', shiftId).single();
  if (!shift) return { error: 'Shift not found' };
  const expectedCash = parseFloat(shift.opening_float || 0) + parseFloat(shift.cash_sales || 0);
  const variance = parseFloat(actualCash) - expectedCash;
  const { data, error } = await supabase
    .from('staff_shifts')
    .update({
      closed_at: new Date().toISOString(),
      actual_cash: actualCash,
      expected_cash: expectedCash,
      variance,
      notes,
      status: 'closed',
    })
    .eq('id', shiftId)
    .select();
  if (error) console.error('closeShift:', error);
  return { data: data?.[0], variance, error };
}

export async function fetchOpenShift(branchId, staffName) {
  const { data } = await supabase
    .from('staff_shifts')
    .select('*')
    .eq('branch_id', branchId)
    .eq('staff_name', staffName)
    .eq('status', 'open')
    .maybeSingle();
  return data;
}

export async function fetchShifts(branchId) {
  let q = supabase.from('staff_shifts').select('*').order('opened_at', { ascending: false }).limit(50);
  if (branchId) q = q.eq('branch_id', branchId);
  const { data } = await q;
  return data || [];
}

export async function updateShiftSales(shiftId, cashAmount, cardAmount, tipAmount) {
  // Increment shift totals
  const { data: shift } = await supabase.from('staff_shifts').select('cash_sales, card_sales, tips_collected').eq('id', shiftId).single();
  if (!shift) return;
  await supabase
    .from('staff_shifts')
    .update({
      cash_sales: parseFloat(shift.cash_sales || 0) + (cashAmount || 0),
      card_sales: parseFloat(shift.card_sales || 0) + (cardAmount || 0),
      tips_collected: parseFloat(shift.tips_collected || 0) + (tipAmount || 0),
    })
    .eq('id', shiftId);
}

// VOIDS & REFUNDS
export async function recordVoid(voidData) {
  // voidData = { order_id, branch_id, void_type, amount, reason, approved_by, staff_member }
  const { data, error } = await supabase.from('order_voids').insert(voidData).select();
  if (error) console.error('recordVoid:', error);
  return { data: data?.[0], error };
}

export async function fetchVoids(branchId) {
  let q = supabase.from('order_voids').select('*').order('created_at', { ascending: false }).limit(100);
  if (branchId) q = q.eq('branch_id', branchId);
  const { data } = await q;
  return data || [];
}

// MANAGER PINS
export async function verifyManagerPin(pin, branchId) {
  let q = supabase.from('manager_pins').select('*').eq('pin_hash', pin).eq('active', true);
  const { data } = await q;
  if (!data || !data.length) return null;
  // Match: prefer exact branch match, fallback to global (null branch_id)
  const branchMatch = data.find(p => p.branch_id === branchId);
  return branchMatch || data.find(p => !p.branch_id) || null;
}

export async function fetchManagerPins(branchId) {
  let q = supabase.from('manager_pins').select('*').eq('active', true);
  if (branchId) q = q.or(`branch_id.eq.${branchId},branch_id.is.null`);
  const { data } = await q;
  return data || [];
}

export async function saveManagerPin(pinData) {
  const { data, error } = await supabase.from('manager_pins').insert(pinData).select();
  if (error) console.error('saveManagerPin:', error);
  return { data: data?.[0], error };
}

// DRAWER EVENTS
export async function recordDrawerEvent(event) {
  const { data, error } = await supabase.from('drawer_events').insert(event).select();
  if (error) console.error('recordDrawerEvent:', error);
  return { data: data?.[0], error };
}

// ===========================================================
// EXPENSE TRACKING
// ===========================================================

// Categories
export async function fetchExpenseCategories(branchId) {
  let q = supabase.from('expense_categories').select('*').eq('restaurant_id', _rid()).eq('active', true).order('display_order');
  // Get global (branch_id IS NULL) and branch-specific
  const { data, error } = await q;
  if (error) console.error('fetchExpenseCategories:', error);
  return data || [];
}

export async function saveExpenseCategory(category) {
  const payload = {
    restaurant_id: _rid(),
    branch_id: category.branchId || null,
    name: category.name,
    icon: category.icon || null,
    color: category.color || '#bf4626',
    description: category.description || null,
    active: category.active !== false,
    display_order: category.displayOrder || 0,
  };
  if (category.id) {
    const { data, error } = await supabase.from('expense_categories')
      .update(payload).eq('id', category.id).select().single();
    return { data, error };
  }
  const { data, error } = await supabase.from('expense_categories')
    .insert(payload).select().single();
  return { data, error };
}

export async function deleteExpenseCategory(id) {
  const { error } = await supabase.from('expense_categories')
    .update({ active: false }).eq('id', id);
  return { error };
}

// Expenses
export async function fetchExpenses(branchId, fromDate, toDate) {
  let q = supabase.from('expenses').select('*').eq('restaurant_id', _rid()).order('expense_date', { ascending: false });
  if (branchId) q = q.eq('branch_id', branchId);
  if (fromDate) q = q.gte('expense_date', fromDate);
  if (toDate) q = q.lte('expense_date', toDate);
  const { data, error } = await q;
  if (error) console.error('fetchExpenses:', error);
  return data || [];
}

export async function saveExpense(expense) {
  const payload = {
    restaurant_id: _rid(),
    branch_id: expense.branchId,
    category_id: expense.categoryId || null,
    category_name: expense.categoryName,
    amount: parseFloat(expense.amount),
    description: expense.description,
    expense_date: expense.expenseDate || new Date().toISOString().split('T')[0],
    recorded_by: expense.recordedBy,
    recorded_by_role: expense.recordedByRole || 'staff',
    receipt_photo_url: expense.receiptPhotoUrl || null,
    is_recurring: expense.isRecurring || false,
    recurring_id: expense.recurringId || null,
    notes: expense.notes || null,
    updated_at: new Date().toISOString(),
  };
  if (expense.id) {
    const { data, error } = await supabase.from('expenses')
      .update(payload).eq('id', expense.id).select().single();
    return { data, error };
  }
  const { data, error } = await supabase.from('expenses')
    .insert(payload).select().single();
  return { data, error };
}

export async function deleteExpense(id) {
  const { error } = await supabase.from('expenses').delete().eq('id', id);
  return { error };
}

// Recurring expenses
export async function fetchRecurringExpenses(branchId) {
  let q = supabase.from('recurring_expenses').select('*').eq('restaurant_id', _rid()).order('created_at');
  if (branchId) q = q.eq('branch_id', branchId);
  const { data } = await q;
  return data || [];
}

export async function saveRecurringExpense(rec) {
  const payload = {
    restaurant_id: _rid(),
    branch_id: rec.branchId,
    category_id: rec.categoryId || null,
    category_name: rec.categoryName,
    amount: parseFloat(rec.amount),
    description: rec.description,
    frequency: rec.frequency || 'monthly',
    day_of_month: rec.dayOfMonth || 1,
    start_date: rec.startDate,
    end_date: rec.endDate || null,
    active: rec.active !== false,
    notes: rec.notes || null,
  };
  if (rec.id) {
    const { data, error } = await supabase.from('recurring_expenses')
      .update(payload).eq('id', rec.id).select().single();
    return { data, error };
  }
  const { data, error } = await supabase.from('recurring_expenses')
    .insert(payload).select().single();
  return { data, error };
}

export async function deleteRecurringExpense(id) {
  const { error } = await supabase.from('recurring_expenses')
    .update({ active: false }).eq('id', id);
  return { error };
}

// Update last_generated_date on a recurring template
export async function updateRecurringLastGenerated(id, dateStr) {
  const { data, error } = await supabase.from('recurring_expenses')
    .update({ last_generated_date: dateStr }).eq('id', id).select().single();
  return { data, error };
}

// ===========================================================
// SAAS MULTI-TENANCY - Restaurant management
// ===========================================================

// Get a restaurant by ID
export async function fetchRestaurant(restaurantId) {
  const { data, error } = await supabase
    .from('restaurants')
    .select('*')
    .eq('id', restaurantId)
    .single();
  if (error) console.error('fetchRestaurant:', error);
  return data;
}

// Get a restaurant by subdomain (e.g., "marios" -> Mario's Pizza)
export async function fetchRestaurantBySubdomain(subdomain) {
  const { data, error } = await supabase
    .from('restaurants')
    .select('*')
    .eq('subdomain', subdomain)
    .single();
  if (error) console.error('fetchRestaurantBySubdomain:', error);
  return data;
}

// Get all restaurants (admin view - you'll use this later)
export async function fetchAllRestaurants() {
  const { data, error } = await supabase
    .from('restaurants')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) console.error('fetchAllRestaurants:', error);
  return data || [];
}

// Update a restaurant
export async function updateRestaurant(id, updates) {
  const { data, error } = await supabase
    .from('restaurants')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

// Create new restaurant (used during signup - Session SaaS-2)
export async function createRestaurant(restaurant) {
  const payload = {
    name: restaurant.name,
    subdomain: restaurant.subdomain,
    owner_email: restaurant.ownerEmail,
    owner_name: restaurant.ownerName,
    phone: restaurant.phone || null,
    cuisine_type: restaurant.cuisineType || 'other',
    plan: 'trial',
    subscription_status: 'trialing',
    trial_ends_at: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    onboarding_complete: false,
  };
  const { data, error } = await supabase
    .from('restaurants')
    .insert(payload)
    .select()
    .single();
  return { data, error };
}

// Check if subdomain is available
export async function isSubdomainAvailable(subdomain) {
  const { data } = await supabase
    .from('restaurants')
    .select('id')
    .eq('subdomain', subdomain)
    .maybeSingle();
  return !data; // available if no result
}

// ===========================================================
// SAAS-2: AUTH (signup, login, email verification)
// ===========================================================

// Generate a random 6-digit verification code
function generateVerificationCode() {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

// Generate a random token (for verification links and password reset)
function generateToken() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token + Date.now();
}

// Generate URL-safe slug from name
function generateSlug(name) {
  return name.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .substring(0, 50);
}

// Sign up new restaurant owner (creates restaurant + owner account)
export async function signupRestaurant({ restaurantName, ownerName, email, password, cuisineType, phone }) {
  // Step 1: Check if email already exists
  const { data: existing } = await supabase
    .from('restaurant_owners')
    .select('id')
    .eq('email', email.toLowerCase().trim())
    .maybeSingle();
  
  if (existing) {
    return { error: { message: 'Email already registered' } };
  }
  
  // Step 2: Generate unique slug
  let slug = generateSlug(restaurantName);
  let slugAttempts = 0;
  while (slugAttempts < 10) {
    const { data: slugCheck } = await supabase
      .from('restaurants')
      .select('id')
      .eq('slug', slug)
      .maybeSingle();
    if (!slugCheck) break;
    slug = generateSlug(restaurantName) + '-' + Math.floor(Math.random() * 1000);
    slugAttempts++;
  }
  
  // Step 3: Create restaurant
  const { data: restaurant, error: restError } = await supabase
    .from('restaurants')
    .insert({
      name: restaurantName,
      slug: slug,
      subdomain: slug,
      owner_email: email.toLowerCase().trim(),
      owner_name: ownerName,
      phone: phone || null,
      plan: 'trial',
      subscription_status: 'trialing',
      trial_ends_at: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      onboarding_complete: false,
      cuisine_type: cuisineType || 'other',
      active: true,
    })
    .select()
    .single();
  
  if (restError) return { error: restError };
  
  // Step 4: Create owner account
  const { data: owner, error: ownerError } = await supabase
    .from('restaurant_owners')
    .insert({
      restaurant_id: restaurant.id,
      email: email.toLowerCase().trim(),
      password_hash: password, // TEMPORARY: plain text for demo. Hash in production.
      full_name: ownerName,
      phone: phone || null,
      email_verified: false,
      active: true,
      is_owner: true,
    })
    .select()
    .single();
  
  if (ownerError) {
    // Rollback: delete restaurant
    await supabase.from('restaurants').delete().eq('id', restaurant.id);
    return { error: ownerError };
  }
  
  // Step 4.5: AUTO-SETUP - Create starter content for new restaurant
  try {
    await setupNewRestaurant(restaurant.id, restaurant.name, cuisineType || 'other');
  } catch (e) {
    console.warn('Auto-setup failed (non-critical):', e);
  }
  
  // Step 5: Create verification token
  const verCode = generateVerificationCode();
  const verToken = generateToken();
  await supabase.from('email_verifications').insert({
    owner_id: owner.id,
    email: owner.email,
    token: verToken,
    verification_code: verCode,
  });
  
  // For demo: log verification code to console (in production, send email)
  console.log('[KEY] Verification code for', email, ':', verCode);
  
  return { 
    data: { 
      restaurant, 
      owner: { ...owner, password_hash: undefined }, 
      verificationCode: verCode 
    } 
  };
}

// Login with email + password
export async function loginRestaurant(email, password) {
  const { data: owner, error } = await supabase
    .from('restaurant_owners')
    .select('*, restaurants(*)')
    .eq('email', email.toLowerCase().trim())
    .eq('active', true)
    .maybeSingle();
  
  if (error || !owner) {
    return { error: { message: 'Invalid email or password' } };
  }
  
  if (owner.password_hash !== password) {
    return { error: { message: 'Invalid email or password' } };
  }
  
  if (!owner.email_verified) {
    return { error: { message: 'Email not verified', needsVerification: true, ownerId: owner.id, email: owner.email } };
  }
  
  // CHECK: Is restaurant suspended?
  if (owner.restaurants && owner.restaurants.active === false) {
    return { error: { message: 'Your account has been suspended. Please contact support at support@latavola.app' } };
  }
  
  // CHECK: Is trial expired?
  if (owner.restaurants && owner.restaurants.plan === 'trial' && owner.restaurants.trial_ends_at) {
    const trialEnd = new Date(owner.restaurants.trial_ends_at);
    if (trialEnd < new Date()) {
      return { error: { message: 'Your free trial has expired. Please upgrade to continue using La Tavola.', subscriptionExpired: true, restaurantName: owner.restaurants.name } };
    }
  }
  
  // CHECK: Is paid subscription expired?
  if (owner.restaurants && owner.restaurants.subscription_ends_at) {
    const subEnd = new Date(owner.restaurants.subscription_ends_at);
    if (subEnd < new Date()) {
      return { error: { message: 'Your subscription has expired. Please renew to continue using La Tavola.', subscriptionExpired: true, restaurantName: owner.restaurants.name } };
    }
  }
  
  // Update last login
  await supabase
    .from('restaurant_owners')
    .update({ last_login_at: new Date().toISOString() })
    .eq('id', owner.id);
  
  // Set current restaurant
  if (owner.restaurants) {
    setCurrentRestaurantId(owner.restaurant_id);
  }
  
  return {
    data: {
      owner: { ...owner, password_hash: undefined, restaurants: undefined },
      restaurant: owner.restaurants,
    }
  };
}

// Verify email with code
export async function verifyEmail(email, code) {
  const { data: verification } = await supabase
    .from('email_verifications')
    .select('*')
    .eq('email', email.toLowerCase().trim())
    .eq('verification_code', code)
    .is('verified_at', null)
    .gt('expires_at', new Date().toISOString())
    .maybeSingle();
  
  if (!verification) {
    return { error: { message: 'Invalid or expired code' } };
  }
  
  // Mark verification as used
  await supabase
    .from('email_verifications')
    .update({ verified_at: new Date().toISOString() })
    .eq('id', verification.id);
  
  // Mark owner as verified
  await supabase
    .from('restaurant_owners')
    .update({ 
      email_verified: true, 
      email_verified_at: new Date().toISOString() 
    })
    .eq('id', verification.owner_id);
  
  return { data: { verified: true, ownerId: verification.owner_id } };
}

// Resend verification code
export async function resendVerification(email) {
  const { data: owner } = await supabase
    .from('restaurant_owners')
    .select('id, email')
    .eq('email', email.toLowerCase().trim())
    .maybeSingle();
  
  if (!owner) return { error: { message: 'Account not found' } };
  
  const verCode = generateVerificationCode();
  const verToken = generateToken();
  await supabase.from('email_verifications').insert({
    owner_id: owner.id,
    email: owner.email,
    token: verToken,
    verification_code: verCode,
  });
  
  console.log('[KEY] New verification code for', email, ':', verCode);
  return { data: { verificationCode: verCode } };
}

// Get current logged-in owner from localStorage
export function getCurrentOwner() {
  try {
    const raw = localStorage.getItem('latavola_saas_owner');
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return null;
}

// Save logged-in owner to localStorage
export function saveCurrentOwner(owner, restaurant) {
  try {
    localStorage.setItem('latavola_saas_owner', JSON.stringify(owner));
    localStorage.setItem('latavola_saas_restaurant', JSON.stringify(restaurant));
    if (restaurant) setCurrentRestaurantId(restaurant.id);
  } catch (e) {}
}

// Logout
export function logoutSaaS() {
  try {
    localStorage.removeItem('latavola_saas_owner');
    localStorage.removeItem('latavola_saas_restaurant');
    // Also clear staff/user session data so refresh doesn't restore a half-state
    localStorage.removeItem('latavola_user');
    localStorage.removeItem('latavola_active_staff');
    localStorage.removeItem('latavola_current_driver');
  } catch (e) {}
}

// Get currently logged-in restaurant
export function getCurrentSaasRestaurant() {
  try {
    const raw = localStorage.getItem('latavola_saas_restaurant');
    if (raw) {
      const r = JSON.parse(raw);
      setCurrentRestaurantId(r.id);
      return r;
    }
  } catch (e) {}
  return null;
}

// Switch to a different restaurant (for testing)
export async function switchRestaurant(restaurantId) {
  const { data: restaurant } = await supabase
    .from('restaurants')
    .select('*')
    .eq('id', restaurantId)
    .single();
  if (restaurant) {
    setCurrentRestaurantId(restaurant.id);
    try {
      localStorage.setItem('latavola_saas_restaurant', JSON.stringify(restaurant));
    } catch (e) {}
    return restaurant;
  }
  return null;
}

// ===========================================================
// AUTO-SETUP: Create starter content for new restaurants
// ===========================================================

async function setupNewRestaurant(restaurantId, restaurantName, cuisineType) {
  console.log('Setting up new restaurant:', restaurantName, 'cuisine:', cuisineType);
  
  // 1. Create default categories (uses display_order, not sort_order)
  const defaultCategories = [
    { name: 'Starters', icon: 'salad', display_order: 1 },
    { name: 'Mains', icon: 'bowl', display_order: 2 },
    { name: 'Drinks', icon: 'glass', display_order: 3 },
    { name: 'Desserts', icon: 'cake', display_order: 4 },
  ];
  
  for (const cat of defaultCategories) {
    try {
      await supabase.from('categories').insert({
        ...cat,
        restaurant_id: restaurantId,
      });
    } catch (e) {
      console.warn('Category insert failed:', cat.name, e);
    }
  }
  
  // 2. Create cuisine-specific sample menu items
  const cuisineMenus = {
    italian: [
      { name: 'Bruschetta', cat: 'Starters', price: 6.50, desc: 'Toasted bread with tomato, garlic, and basil' },
      { name: 'Margherita Pizza', cat: 'Mains', price: 9.99, desc: 'Tomato, mozzarella, fresh basil' },
      { name: 'Spaghetti Carbonara', cat: 'Mains', price: 12.50, desc: 'Bacon, egg, pecorino, black pepper' },
      { name: 'Tiramisu', cat: 'Desserts', price: 5.50, desc: 'Classic Italian dessert' },
      { name: 'Italian Soda', cat: 'Drinks', price: 3.00, desc: 'Sparkling water with syrup' },
    ],
    indian: [
      { name: 'Onion Bhaji', cat: 'Starters', price: 4.50, desc: 'Crispy onion fritters' },
      { name: 'Chicken Tikka Masala', cat: 'Mains', price: 11.50, desc: 'Creamy tomato curry with chicken' },
      { name: 'Lamb Biryani', cat: 'Mains', price: 13.99, desc: 'Aromatic basmati rice with tender lamb' },
      { name: 'Gulab Jamun', cat: 'Desserts', price: 4.50, desc: 'Sweet milk dumplings' },
      { name: 'Mango Lassi', cat: 'Drinks', price: 3.50, desc: 'Yogurt drink with mango' },
    ],
    chinese: [
      { name: 'Spring Rolls', cat: 'Starters', price: 5.50, desc: 'Crispy vegetable spring rolls' },
      { name: 'Sweet and Sour Chicken', cat: 'Mains', price: 10.50, desc: 'Crispy chicken with sweet and sour sauce' },
      { name: 'Beef Chow Mein', cat: 'Mains', price: 11.99, desc: 'Stir-fried noodles with beef' },
      { name: 'Banana Fritters', cat: 'Desserts', price: 5.00, desc: 'Crispy banana with honey' },
      { name: 'Jasmine Tea', cat: 'Drinks', price: 2.50, desc: 'Traditional Chinese tea' },
    ],
    british: [
      { name: 'Soup of the Day', cat: 'Starters', price: 5.50, desc: 'Homemade with crusty bread' },
      { name: 'Fish and Chips', cat: 'Mains', price: 13.50, desc: 'Beer-battered cod with chips' },
      { name: 'Sunday Roast', cat: 'Mains', price: 15.99, desc: 'Roast beef with all the trimmings' },
      { name: 'Sticky Toffee Pudding', cat: 'Desserts', price: 6.50, desc: 'With vanilla ice cream' },
      { name: 'Builders Tea', cat: 'Drinks', price: 2.00, desc: 'Strong English tea' },
    ],
    american: [
      { name: 'Mozzarella Sticks', cat: 'Starters', price: 6.50, desc: 'Crispy fried with marinara sauce' },
      { name: 'Classic Burger', cat: 'Mains', price: 9.99, desc: 'Beef patty, lettuce, tomato, onion' },
      { name: 'BBQ Bacon Burger', cat: 'Mains', price: 11.99, desc: 'BBQ sauce, bacon, cheddar' },
      { name: 'Apple Pie', cat: 'Desserts', price: 5.50, desc: 'With vanilla ice cream' },
      { name: 'Soft Drink', cat: 'Drinks', price: 2.50, desc: 'Coke, Sprite, or Fanta' },
    ],
    mexican: [
      { name: 'Nachos', cat: 'Starters', price: 6.99, desc: 'With cheese, salsa, guacamole' },
      { name: 'Beef Tacos', cat: 'Mains', price: 9.50, desc: '3 soft tacos with seasoned beef' },
      { name: 'Chicken Burrito', cat: 'Mains', price: 10.99, desc: 'Rice, beans, chicken, salsa' },
      { name: 'Churros', cat: 'Desserts', price: 5.50, desc: 'Cinnamon sugar with chocolate sauce' },
      { name: 'Horchata', cat: 'Drinks', price: 3.00, desc: 'Sweet rice and cinnamon drink' },
    ],
    japanese: [
      { name: 'Edamame', cat: 'Starters', price: 4.50, desc: 'Steamed soybeans with sea salt' },
      { name: 'Salmon Sushi (8 pcs)', cat: 'Mains', price: 12.99, desc: 'Fresh salmon nigiri and rolls' },
      { name: 'Chicken Teriyaki', cat: 'Mains', price: 11.50, desc: 'Grilled chicken with teriyaki sauce' },
      { name: 'Mochi Ice Cream', cat: 'Desserts', price: 5.00, desc: '3 pieces, assorted flavors' },
      { name: 'Green Tea', cat: 'Drinks', price: 2.50, desc: 'Traditional Japanese tea' },
    ],
    thai: [
      { name: 'Spring Rolls', cat: 'Starters', price: 5.50, desc: 'Crispy with sweet chili sauce' },
      { name: 'Pad Thai', cat: 'Mains', price: 10.99, desc: 'Stir-fried rice noodles with peanuts' },
      { name: 'Green Curry', cat: 'Mains', price: 11.99, desc: 'Spicy curry with coconut milk' },
      { name: 'Mango Sticky Rice', cat: 'Desserts', price: 5.50, desc: 'Sweet sticky rice with mango' },
      { name: 'Thai Iced Tea', cat: 'Drinks', price: 3.00, desc: 'Sweet tea with milk' },
    ],
    cafe: [
      { name: 'Avocado Toast', cat: 'Starters', price: 7.50, desc: 'Sourdough with smashed avocado' },
      { name: 'Club Sandwich', cat: 'Mains', price: 8.99, desc: 'Chicken, bacon, lettuce, tomato' },
      { name: 'Grilled Panini', cat: 'Mains', price: 7.50, desc: 'Cheese and ham toasted panini' },
      { name: 'Carrot Cake', cat: 'Desserts', price: 4.50, desc: 'Slice with cream cheese frosting' },
      { name: 'Latte', cat: 'Drinks', price: 3.50, desc: 'Espresso with steamed milk' },
    ],
    other: [
      { name: 'Side Salad', cat: 'Starters', price: 5.00, desc: 'Mixed greens with dressing' },
      { name: 'Daily Special', cat: 'Mains', price: 11.99, desc: 'Ask staff for todays special' },
      { name: 'House Pasta', cat: 'Mains', price: 9.99, desc: 'Chefs choice pasta dish' },
      { name: 'Chocolate Brownie', cat: 'Desserts', price: 5.50, desc: 'Warm with vanilla ice cream' },
      { name: 'Soft Drink', cat: 'Drinks', price: 2.50, desc: 'Coke, Sprite, or water' },
    ],
  };
  
  const menuItems = cuisineMenus[cuisineType] || cuisineMenus.other;
  
  for (const item of menuItems) {
    try {
      await supabase.from('menu_items').insert({
        name: item.name,
        category_name: item.cat,
        price: item.price,
        price_dinein: item.price,
        price_takeaway: item.price,
        price_delivery: item.price,
        description: item.desc,
        available: true,
        avail_dinein: true,
        avail_takeaway: true,
        avail_delivery: true,
        stock: 99,
        icon: 'cart',
        restaurant_id: restaurantId,
      });
    } catch (e) {
      console.warn('Menu item insert failed:', item.name, e);
    }
  }
  
  // 3. Create default tables (table is restaurant_tables)
  const branchId = 'b-' + restaurantId.substring(0, 8);
  for (let i = 1; i <= 6; i++) {
    try {
      await supabase.from('restaurant_tables').insert({
        table_number: 'T' + i,
        seats: i === 3 ? 2 : (i === 4 ? 6 : (i === 6 ? 8 : 4)),
        status: 'free',
        branch_id: branchId,
        x_pos: 100 + ((i - 1) % 3) * 100,
        y_pos: 100 + Math.floor((i - 1) / 3) * 100,
        restaurant_id: restaurantId,
      });
    } catch (e) {
      console.warn('Table insert failed:', i, e);
    }
  }
  
  // 4. Create default manager PIN (1234)
  try {
    await supabase.from('manager_pins').insert({
      pin_hash: '1234',
      label: 'Manager',
      active: true,
      restaurant_id: restaurantId,
    });
  } catch (e) {
    console.warn('Manager PIN insert failed:', e);
  }
  
  // 5. Create default expense categories
  const expCategories = [
    { name: 'Food & Beverages', icon: 'food', color: '#dc2626', display_order: 1 },
    { name: 'Utilities', icon: 'bolt', color: '#0891b2', display_order: 2 },
    { name: 'Rent', icon: 'building', color: '#7c3aed', display_order: 3 },
    { name: 'Salaries', icon: 'users', color: '#059669', display_order: 4 },
    { name: 'Equipment', icon: 'wrench', color: '#d97706', display_order: 5 },
    { name: 'Marketing', icon: 'speaker', color: '#be185d', display_order: 6 },
    { name: 'Cleaning', icon: 'broom', color: '#0d9488', display_order: 7 },
    { name: 'Other', icon: 'box', color: '#6b7280', display_order: 8 },
  ];
  
  for (const cat of expCategories) {
    try {
      await supabase.from('expense_categories').insert({
        ...cat,
        active: true,
        restaurant_id: restaurantId,
      });
    } catch (e) {
      console.warn('Expense category insert failed:', cat.name, e);
    }
  }
  
  console.log('Auto-setup complete for', restaurantName);
}

// ===========================================================
// PHASE A: URL ROUTING - Find restaurants for customer ordering
// ===========================================================

// Detect restaurant from URL (path, subdomain, or query param)
export async function detectRestaurantFromUrl() {
  if (typeof window === 'undefined') return null;
  
  try {
    // 1. Check ?r= query param first (highest priority)
    const params = new URLSearchParams(window.location.search);
    const queryRestaurant = params.get('r') || params.get('restaurant');
    if (queryRestaurant) {
      const r = await fetchRestaurantBySlug(queryRestaurant);
      if (r) {
        setCurrentRestaurantId(r.id); // CRITICAL: tell queries which restaurant
        return { source: 'query', restaurant: r };
      }
    }
    
    // 2. Check subdomain (e.g., marios.latavola.app)
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    if (parts.length > 2 && parts[0] !== 'www') {
      // Has a subdomain
      const subdomain = parts[0];
      // Skip vercel app subdomains
      if (subdomain !== 'la-tavola-xi') {
        const r = await fetchRestaurantBySubdomain(subdomain);
        if (r) {
          setCurrentRestaurantId(r.id);
          return { source: 'subdomain', restaurant: r };
        }
      }
    }
    
    // 3. Check URL path (e.g., /marios)
    const path = window.location.pathname;
    const pathParts = path.split('/').filter(p => p);
    if (pathParts.length > 0) {
      const slug = pathParts[0];
      // Skip system paths
      const systemPaths = ['signup', 'login', 'admin', 'api', 'static'];
      if (!systemPaths.includes(slug)) {
        const r = await fetchRestaurantBySlug(slug);
        if (r) {
          setCurrentRestaurantId(r.id);
          return { source: 'path', restaurant: r };
        }
      }
    }
  } catch (e) {
    console.warn('Restaurant URL detection error:', e);
  }
  
  return null;
}

// Find restaurant by slug
export async function fetchRestaurantBySlug(slug) {
  if (!slug) return null;
  const { data } = await supabase
    .from('restaurants')
    .select('*')
    .eq('slug', slug.toLowerCase().trim())
    .maybeSingle();
  return data;
}

// Get all active restaurants (for directory page)
export async function fetchPublicRestaurants() {
  const { data } = await supabase
    .from('restaurants')
    .select('id, name, slug, subdomain, cuisine_type, brand_color, address, phone')
    .eq('active', true)
    .order('name');
  return data || [];
}

// Update restaurant's online ordering settings
export async function updateRestaurantOrderTypes(restaurantId, settings) {
  const { data, error } = await supabase
    .from('restaurants')
    .update({
      enable_dinein: settings.dinein !== false,
      enable_takeaway: settings.takeaway !== false,
      enable_delivery: settings.delivery !== false,
      enable_online_ordering: settings.onlineOrdering !== false,
    })
    .eq('id', restaurantId)
    .select()
    .single();
  return { data, error };
}

// ===========================================================
// SUPER ADMIN PANEL - Platform owner functionality
// ===========================================================

// Check if currently logged in user is a super admin
export async function isSuperAdmin() {
  const owner = getCurrentOwner();
  if (!owner) return false;
  try {
    const { data } = await supabase
      .from('restaurant_owners')
      .select('is_super_admin')
      .eq('id', owner.id)
      .maybeSingle();
    return data?.is_super_admin === true;
  } catch (e) {
    return false;
  }
}

// Get all restaurants with comprehensive stats for super admin panel
export async function fetchAllRestaurantsWithStats() {
  try {
    // Get all restaurants
    const { data: restaurants } = await supabase
      .from('restaurants')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (!restaurants) return [];
    
    // For each restaurant, get stats
    const enriched = await Promise.all(restaurants.map(async (r) => {
      const stats = {};
      
      // Get menu count
      const { count: menuCount } = await supabase
        .from('menu_items')
        .select('*', { count: 'exact', head: true })
        .eq('restaurant_id', r.id);
      stats.menu_count = menuCount || 0;
      
      // Get table count
      const { count: tableCount } = await supabase
        .from('restaurant_tables')
        .select('*', { count: 'exact', head: true })
        .eq('restaurant_id', r.id);
      stats.table_count = tableCount || 0;
      
      // Get order count + revenue
      const { data: orders } = await supabase
        .from('orders')
        .select('total, paid, status, refunded, voided, created_at')
        .eq('restaurant_id', r.id);
      
      stats.orders_total = orders?.length || 0;
      stats.revenue = (orders || [])
        .filter(o => o.paid && o.status !== 'cancelled' && !o.refunded && !o.voided)
        .reduce((s, o) => s + (parseFloat(o.total) || 0), 0);
      
      // Last order date
      const lastOrder = (orders || []).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      )[0];
      stats.last_activity = lastOrder?.created_at || r.updated_at || r.created_at;
      
      // Get owner info
      const { data: owner } = await supabase
        .from('restaurant_owners')
        .select('email, full_name, last_login')
        .eq('restaurant_id', r.id)
        .maybeSingle();
      stats.owner_email = owner?.email || r.owner_email || 'unknown';
      stats.owner_name = owner?.full_name || r.owner_name || 'Unknown';
      
      // Trial calculations
      if (r.plan === 'trial' && r.trial_ends_at) {
        const trialEnd = new Date(r.trial_ends_at);
        const now = new Date();
        const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
        stats.trial_days_left = daysLeft;
        stats.trial_status = daysLeft < 0 ? 'expired' : daysLeft <= 3 ? 'urgent' : daysLeft <= 7 ? 'warning' : 'ok';
      }
      
      return { ...r, stats };
    }));
    
    return enriched;
  } catch (e) {
    console.error('fetchAllRestaurantsWithStats error:', e);
    return [];
  }
}

// Calculate platform-wide stats
export async function fetchPlatformStats() {
  try {
    const { data: restaurants } = await supabase
      .from('restaurants')
      .select('plan, trial_ends_at, active, created_at');
    
    if (!restaurants) return {};
    
    const now = new Date();
    const planPrices = { starter: 29, pro: 69, enterprise: 149 };
    
    const stats = {
      total: restaurants.length,
      active: restaurants.filter(r => r.active !== false).length,
      trial: restaurants.filter(r => r.plan === 'trial').length,
      paid: restaurants.filter(r => r.plan && r.plan !== 'trial').length,
      mrr: restaurants
        .filter(r => r.plan && r.plan !== 'trial' && r.active !== false)
        .reduce((s, r) => s + (planPrices[r.plan] || 0), 0),
      signups_last_7d: restaurants.filter(r => {
        const created = new Date(r.created_at);
        return (now - created) / (1000 * 60 * 60 * 24) <= 7;
      }).length,
      signups_last_30d: restaurants.filter(r => {
        const created = new Date(r.created_at);
        return (now - created) / (1000 * 60 * 60 * 24) <= 30;
      }).length,
      trials_expiring_soon: restaurants.filter(r => {
        if (r.plan !== 'trial' || !r.trial_ends_at) return false;
        const days = (new Date(r.trial_ends_at) - now) / (1000 * 60 * 60 * 24);
        return days >= 0 && days <= 3;
      }).length,
    };
    
    return stats;
  } catch (e) {
    console.error('fetchPlatformStats error:', e);
    return {};
  }
}

// Log an admin action
export async function logPlatformActivity(adminEmail, action, restaurantId, restaurantName, details = {}) {
  try {
    await supabase.from('platform_activity').insert({
      admin_email: adminEmail,
      action: action,
      target_restaurant_id: restaurantId,
      target_restaurant_name: restaurantName,
      details: details,
    });
  } catch (e) {
    console.warn('Failed to log activity:', e);
  }
}

// Get recent platform activity
export async function fetchPlatformActivity(limit = 50) {
  try {
    const { data } = await supabase
      .from('platform_activity')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    return data || [];
  } catch (e) {
    return [];
  }
}

// Update restaurant plan (manual billing)
export async function updateRestaurantPlan(restaurantId, plan, adminEmail) {
  try {
    const { data, error } = await supabase
      .from('restaurants')
      .update({ plan: plan, updated_at: new Date().toISOString() })
      .eq('id', restaurantId)
      .select()
      .single();
    
    if (data) {
      await logPlatformActivity(adminEmail, 'plan_changed', restaurantId, data.name, { new_plan: plan });
    }
    return { data, error };
  } catch (e) {
    return { error: e };
  }
}

// Toggle restaurant active status (suspend/activate)
export async function toggleRestaurantActive(restaurantId, active, adminEmail) {
  try {
    const { data, error } = await supabase
      .from('restaurants')
      .update({ active: active, updated_at: new Date().toISOString() })
      .eq('id', restaurantId)
      .select()
      .single();
    
    if (data) {
      await logPlatformActivity(adminEmail, active ? 'restaurant_activated' : 'restaurant_suspended', restaurantId, data.name);
    }
    return { data, error };
  } catch (e) {
    return { error: e };
  }
}

// Impersonate a restaurant (login as them for support)
export async function impersonateRestaurant(restaurantId, adminEmail) {
  try {
    const { data: restaurant } = await supabase
      .from('restaurants')
      .select('*')
      .eq('id', restaurantId)
      .single();
    
    if (restaurant) {
      // Save current admin session for restoration
      const currentOwner = getCurrentOwner();
      try {
        localStorage.setItem('latavola_admin_backup', JSON.stringify({
          owner: currentOwner,
          restaurant: getCurrentSaasRestaurant(),
          timestamp: new Date().toISOString(),
        }));
        // Switch to target restaurant
        localStorage.setItem('latavola_saas_restaurant', JSON.stringify(restaurant));
        // Mark that we're in impersonation mode
        localStorage.setItem('latavola_impersonating', JSON.stringify({
          target_id: restaurant.id,
          target_name: restaurant.name,
          admin_email: adminEmail,
          started_at: new Date().toISOString(),
        }));
      } catch (e) {}
      
      setCurrentRestaurantId(restaurant.id);
      await logPlatformActivity(adminEmail, 'impersonate_started', restaurantId, restaurant.name);
      return restaurant;
    }
    return null;
  } catch (e) {
    console.error('Impersonation failed:', e);
    return null;
  }
}

// Stop impersonation - restore original admin session
export async function stopImpersonation() {
  try {
    const backup = localStorage.getItem('latavola_admin_backup');
    const impersonation = localStorage.getItem('latavola_impersonating');
    
    if (impersonation) {
      const imp = JSON.parse(impersonation);
      await logPlatformActivity(imp.admin_email, 'impersonate_ended', imp.target_id, imp.target_name);
    }
    
    if (backup) {
      const data = JSON.parse(backup);
      if (data.restaurant) {
        localStorage.setItem('latavola_saas_restaurant', JSON.stringify(data.restaurant));
        setCurrentRestaurantId(data.restaurant.id);
      }
    }
    
    localStorage.removeItem('latavola_admin_backup');
    localStorage.removeItem('latavola_impersonating');
    localStorage.removeItem('latavola_branch');
    localStorage.removeItem('latavola_user');
  } catch (e) {
    console.error('Stop impersonation error:', e);
  }
}

// Check if currently impersonating
export function isImpersonating() {
  try {
    return JSON.parse(localStorage.getItem('latavola_impersonating') || 'null');
  } catch (e) {
    return null;
  }
}

// ===========================================================
// SUPER ADMIN: RESTAURANT MANAGEMENT (Create/Edit/Delete)
// ===========================================================

// Generate random password
function generateRandomPassword() {
  const chars = 'ABCDEFGHIJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let pwd = '';
  for (let i = 0; i < 10; i++) {
    pwd += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return pwd;
}

// Generate slug from restaurant name
function slugifyName(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .substring(0, 50);
}

// CREATE: Super admin creates a new restaurant fully set up
export async function adminCreateRestaurant(formData, adminEmail) {
  try {
    // Generate slug if not provided
    let slug = formData.slug || slugifyName(formData.name);
    let subdomain = formData.subdomain || slug.replace(/-/g, '');
    
    // Check if slug already exists
    const { data: existing } = await supabase
      .from('restaurants')
      .select('id')
      .eq('slug', slug)
      .maybeSingle();
    
    if (existing) {
      // Add suffix to make unique
      slug = slug + '-' + Date.now().toString().substring(8);
      subdomain = subdomain + Date.now().toString().substring(10);
    }
    
    // Generate password if not provided
    const password = formData.password || generateRandomPassword();
    
    // Calculate trial end date
    const trialDays = parseInt(formData.trial_days) || 30;
    const trialEnd = new Date();
    trialEnd.setDate(trialEnd.getDate() + trialDays);
    
    // 1. Create restaurant
    const { data: restaurant, error: restError } = await supabase
      .from('restaurants')
      .insert({
        name: formData.name,
        slug: slug,
        subdomain: subdomain,
        cuisine_type: formData.cuisine_type || 'general',
        brand_color: formData.brand_color || '#bf4626',
        address: formData.address || '',
        postcode: formData.postcode || '',
        phone: formData.phone || '',
        lat: formData.lat || null,
        lng: formData.lng || null,
        plan: formData.plan || 'trial',
        active: formData.active !== false,
        trial_ends_at: formData.plan === 'trial' ? trialEnd.toISOString() : null,
        enable_online_ordering: true,
        show_in_directory: true,
        owner_email: formData.owner_email,
        owner_name: formData.owner_name,
      })
      .select()
      .single();
    
    if (restError) throw restError;
    
    // 2. Create owner account (matches working signupRestaurant format)
    const ownerData = {
      restaurant_id: restaurant.id,
      email: formData.owner_email.toLowerCase().trim(),
      password_hash: password, // Matches existing schema
      full_name: formData.owner_name,
      phone: formData.phone || null,
      email_verified: true, // Skip verification - admin created
      active: true,
      is_owner: true,
    };
    
    // Try with optional super-admin fields
    try {
      ownerData.is_super_admin = false;
    } catch (e) {}
    try {
      ownerData.onboarding_completed = formData.skip_onboarding !== false;
    } catch (e) {}
    
    let { data: owner, error: ownerError } = await supabase
      .from('restaurant_owners')
      .insert(ownerData)
      .select()
      .single();
    
    // Retry without optional columns if they don't exist
    if (ownerError && ownerError.message) {
      const msg = ownerError.message;
      if (msg.includes('onboarding_completed')) delete ownerData.onboarding_completed;
      if (msg.includes('is_super_admin')) delete ownerData.is_super_admin;
      
      const retry = await supabase
        .from('restaurant_owners')
        .insert(ownerData)
        .select()
        .single();
      owner = retry.data;
      ownerError = retry.error;
    }
    
    if (ownerError) {
      // Rollback - delete the restaurant we just created
      await supabase.from('restaurants').delete().eq('id', restaurant.id);
      throw ownerError;
    }
    
    // 3. Auto-setup: Create default categories, menu, tables
    if (formData.auto_setup !== false) {
      // Categories
      const categories = [
        { restaurant_id: restaurant.id, name: 'Starters', display_order: 1, active: true },
        { restaurant_id: restaurant.id, name: 'Main Course', display_order: 2, active: true },
        { restaurant_id: restaurant.id, name: 'Desserts', display_order: 3, active: true },
        { restaurant_id: restaurant.id, name: 'Drinks', display_order: 4, active: true },
      ];
      const { data: createdCats } = await supabase
        .from('categories')
        .insert(categories)
        .select();
      
      // Menu items - sample
      if (createdCats && createdCats.length > 0) {
        const sampleMenu = [
          { name: 'Sample Starter', price: 5.95, cat: createdCats[0].id },
          { name: 'Sample Main 1', price: 12.95, cat: createdCats[1].id },
          { name: 'Sample Main 2', price: 14.95, cat: createdCats[1].id },
          { name: 'Sample Dessert', price: 5.95, cat: createdCats[2].id },
          { name: 'Sample Drink', price: 2.95, cat: createdCats[3].id },
        ];
        
        const menuItems = sampleMenu.map((m, i) => ({
          restaurant_id: restaurant.id,
          category_id: m.cat,
          item_name: m.name,
          description: 'Edit this item or add your own',
          price_dinein: m.price,
          price_takeaway: m.price,
          price_delivery: m.price,
          active: true,
          display_order: i + 1,
        }));
        
        await supabase.from('menu_items').insert(menuItems);
      }
      
      // Tables - using actual column names from your DB
      const tables = [];
      for (let i = 1; i <= 6; i++) {
        const xPos = 100 + ((i - 1) % 3) * 150;  // 100, 250, 400, 100, 250, 400
        const yPos = 100 + Math.floor((i - 1) / 3) * 150;  // 100, 100, 100, 250, 250, 250
        tables.push({
          restaurant_id: restaurant.id,
          branch_id: 'main',
          number: 'T' + i,
          table_number: i,
          capacity: 4,
          seats: 4,
          status: 'available',
          position_x: xPos,
          position_y: yPos,
          x_pos: xPos,
          y_pos: yPos,
        });
      }
      try {
        await supabase.from('restaurant_tables').insert(tables);
      } catch (e) {
        console.warn('Failed to create tables:', e);
      }
      
      // Manager PIN
      await supabase.from('manager_pins').insert({
        restaurant_id: restaurant.id,
        pin: '1234',
        manager_name: formData.owner_name,
        active: true,
      });
      
      // Default delivery settings
      await supabase.from('delivery_settings').insert({
        restaurant_id: restaurant.id,
        branch_id: 'main',
        method: 'radius',
        max_radius_miles: 5,
        flat_fee: 2.50,
        min_order_amount: 15,
        free_delivery_above: 25,
        is_active: true,
        accept_cash: true,
      });
      
      // Expense categories
      const expenseCats = [
        { restaurant_id: restaurant.id, name: 'Ingredients', icon: 'food' },
        { restaurant_id: restaurant.id, name: 'Utilities', icon: 'lightning' },
        { restaurant_id: restaurant.id, name: 'Rent', icon: 'home' },
        { restaurant_id: restaurant.id, name: 'Staff', icon: 'people' },
        { restaurant_id: restaurant.id, name: 'Other', icon: 'misc' },
      ];
      await supabase.from('expense_categories').insert(expenseCats);
    }
    
    // Log activity
    await logPlatformActivity(adminEmail, 'restaurant_created_by_admin', restaurant.id, restaurant.name, {
      plan: formData.plan,
      auto_setup: formData.auto_setup !== false,
    });
    
    return {
      restaurant,
      owner,
      password,
      slug,
      subdomain,
      success: true,
    };
  } catch (e) {
    console.error('adminCreateRestaurant error:', e);
    return { error: e.message || 'Failed to create restaurant', success: false };
  }
}

// EDIT: Update restaurant fields
export async function adminUpdateRestaurant(restaurantId, updates, adminEmail) {
  try {
    const { data, error } = await supabase
      .from('restaurants')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', restaurantId)
      .select()
      .single();
    
    if (error) throw error;
    
    await logPlatformActivity(adminEmail, 'restaurant_edited_by_admin', restaurantId, data.name, {
      changed_fields: Object.keys(updates),
    });
    
    return { data, success: true };
  } catch (e) {
    return { error: e.message, success: false };
  }
}

// DELETE: Permanently remove restaurant and all its data
export async function adminDeleteRestaurant(restaurantId, restaurantName, adminEmail) {
  try {
    console.log('=== DELETE START ===');
    console.log('Restaurant ID:', restaurantId);
    console.log('Restaurant Name:', restaurantName);
    
    // Log first (before deletion)
    await logPlatformActivity(adminEmail, 'restaurant_deleted_by_admin', null, restaurantName, {
      deleted_restaurant_id: restaurantId,
    });
    console.log('Activity logged');
    
    // Verify the restaurant exists before delete
    const { data: existsBefore } = await supabase
      .from('restaurants')
      .select('id, name')
      .eq('id', restaurantId)
      .maybeSingle();
    console.log('Before delete - exists:', existsBefore);
    
    if (!existsBefore) {
      console.log('Restaurant does not exist - already deleted?');
      return { success: true };
    }
    
    // Try the delete
    const result = await supabase
      .from('restaurants')
      .delete()
      .eq('id', restaurantId);
    
    console.log('Delete result:', result);
    console.log('Delete error:', result.error);
    console.log('Delete status:', result.status);
    console.log('Delete count:', result.count);
    
    if (result.error) {
      console.error('Delete failed with error:', result.error);
      throw result.error;
    }
    
    // Wait a moment for DB to propagate
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Check if it actually got deleted
    const { data: existsAfter } = await supabase
      .from('restaurants')
      .select('id')
      .eq('id', restaurantId)
      .maybeSingle();
    
    console.log('After delete - exists:', existsAfter);
    console.log('=== DELETE END ===');
    
    if (existsAfter) {
      // Still exists - something is preventing the delete
      throw new Error('Delete returned success but restaurant still exists. This usually means RLS policies are blocking the delete. Check Supabase RLS policies on the restaurants table.');
    }
    
    return { success: true };
  } catch (e) {
    console.error('adminDeleteRestaurant error:', e);
    return { error: e.message || 'Unknown error', success: false };
  }
}

// Reset restaurant owner password (for support)
export async function adminResetOwnerPassword(restaurantId, newPassword, adminEmail) {
  try {
    const password = newPassword || generateRandomPassword();
    
    const { data, error } = await supabase
      .from('restaurant_owners')
      .update({ password_hash: password })
      .eq('restaurant_id', restaurantId)
      .select()
      .single();
    
    if (error) throw error;
    
    await logPlatformActivity(adminEmail, 'password_reset_by_admin', restaurantId, null, {
      owner_email: data.email,
    });
    
    return { data, password, success: true };
  } catch (e) {
    return { error: e.message, success: false };
  }
}

// ===========================================================
// STAFF MANAGEMENT MODULE
// ===========================================================

// Generate next employee ID (EMP001, EMP002, etc.)
async function generateEmployeeId(restaurantId) {
  const { data } = await supabase
    .from('employees')
    .select('employee_id')
    .eq('restaurant_id', restaurantId)
    .order('created_at', { ascending: false })
    .limit(1);
  
  if (!data || data.length === 0) return 'EMP001';
  const last = data[0].employee_id || 'EMP000';
  const num = parseInt(last.replace(/\D/g, '')) || 0;
  return 'EMP' + String(num + 1).padStart(3, '0');
}

// Generate unique 4-digit PIN
async function generateUniquePIN(restaurantId) {
  for (let attempt = 0; attempt < 50; attempt++) {
    const pin = String(Math.floor(1000 + Math.random() * 9000));
    const { data } = await supabase
      .from('employees')
      .select('id')
      .eq('restaurant_id', restaurantId)
      .eq('pin', pin)
      .maybeSingle();
    if (!data) return pin;
  }
  return String(Math.floor(1000 + Math.random() * 9000));
}

// FETCH all staff members
export async function fetchStaffMembers(includeInactive = false) {
  let q = supabase
    .from('employees')
    .select('*')
    .eq('restaurant_id', _rid())
    .order('full_name');
  
  if (!includeInactive) q = q.eq('status', 'active');
  
  const { data, error } = await q;
  if (error) console.error('fetchStaffMembers:', error);
  return data || [];
}

// FETCH single staff member by ID
export async function fetchStaffMember(staffId) {
  const { data, error } = await supabase
    .from('employees')
    .select('*')
    .eq('id', staffId)
    .eq('restaurant_id', _rid())
    .single();
  if (error) console.error('fetchStaffMember:', error);
  return data;
}

// FETCH drivers only (for delivery assignment)
export async function fetchDrivers() {
  const { data, error } = await supabase
    .from('employees')
    .select('*')
    .eq('restaurant_id', _rid())
    .eq('position', 'driver')
    .eq('status', 'active')
    .eq('is_available_for_delivery', true)
    .order('full_name');
  if (error) console.error('fetchDrivers:', error);
  return data || [];
}

// CREATE staff member
export async function createStaffMember(staff) {
  const restaurantId = _rid();
  
  // Auto-generate employee ID and PIN if not provided
  const employeeId = staff.employee_id || await generateEmployeeId(restaurantId);
  const pin = staff.pin || await generateUniquePIN(restaurantId);
  
  const payload = {
    restaurant_id: restaurantId,
    branch_id: staff.branch_id || 'main',
    
    // Personal
    full_name: staff.full_name,
    email: staff.email || null,
    phone: staff.phone || null,
    address: staff.address || null,
    postcode: staff.postcode || null,
    date_of_birth: staff.date_of_birth || null,
    emergency_contact_name: staff.emergency_contact_name || null,
    emergency_contact_phone: staff.emergency_contact_phone || null,
    national_insurance: staff.national_insurance || null,
    photo_url: staff.photo_url || null,
    
    // Employment
    position: staff.position || 'waiter',
    employee_id: employeeId,
    start_date: staff.start_date || new Date().toISOString().split('T')[0],
    status: staff.status || 'active',
    pin: pin,
    
    // Compensation
    payment_type: staff.payment_type || 'hourly',
    hourly_rate: staff.hourly_rate ? parseFloat(staff.hourly_rate) : null,
    monthly_salary: staff.monthly_salary ? parseFloat(staff.monthly_salary) : null,
    commission_rate: staff.commission_rate ? parseFloat(staff.commission_rate) : null,
    payment_method: staff.payment_method || 'bank',
    bank_account: staff.bank_account || null,
    bank_sort_code: staff.bank_sort_code || null,
    
    // Permissions
    permissions: staff.permissions || {
      take_orders: true,
      view_reports: false,
      manage_menu: false,
      process_refunds: false,
      manage_staff: false,
      manage_settings: false,
      view_finance: false
    },
    
    // Driver fields
    driver_license_number: staff.driver_license_number || null,
    driver_license_expiry: staff.driver_license_expiry || null,
    vehicle_make: staff.vehicle_make || null,
    vehicle_model: staff.vehicle_model || null,
    vehicle_registration: staff.vehicle_registration || null,
    vehicle_color: staff.vehicle_color || null,
    insurance_provider: staff.insurance_provider || null,
    insurance_policy_number: staff.insurance_policy_number || null,
    insurance_expiry: staff.insurance_expiry || null,
    delivery_zone: staff.delivery_zone || null,
    is_available_for_delivery: staff.is_available_for_delivery !== false,
    
    notes: staff.notes || null,
  };
  
  const { data, error } = await supabase
    .from('employees')
    .insert(payload)
    .select()
    .single();
  
  if (error) {
    console.error('createStaffMember error:', error);
    return { error };
  }
  
  return { data, success: true };
}

// UPDATE staff member
export async function updateStaffMember(staffId, updates) {
  // Remove fields that shouldn't be updated
  const { id, created_at, updated_at, restaurant_id, restaurants, ...cleanUpdates } = updates;
  
  const payload = { ...cleanUpdates, updated_at: new Date().toISOString() };
  
  // Convert numeric strings
  if (payload.hourly_rate==='') payload.hourly_rate = null;
  else if (payload.hourly_rate) payload.hourly_rate = parseFloat(payload.hourly_rate);
  if (payload.monthly_salary==='') payload.monthly_salary = null;
  else if (payload.monthly_salary) payload.monthly_salary = parseFloat(payload.monthly_salary);
  if (payload.commission_rate==='') payload.commission_rate = null;
  else if (payload.commission_rate) payload.commission_rate = parseFloat(payload.commission_rate);
  
  // Convert empty strings to null for date fields
  const dateFields = ['date_of_birth', 'start_date', 'end_date', 'driver_license_expiry', 'insurance_expiry'];
  dateFields.forEach(field => {
    if (payload[field] === '') payload[field] = null;
  });
  
  let { data, error } = await supabase
    .from('employees')
    .update(payload)
    .eq('id', staffId)
    .eq('restaurant_id', _rid())
    .select()
    .single();
  
  // If error mentions a missing column, retry with only the essential fields
  if (error && error.message && error.message.toLowerCase().includes('column')) {
    console.warn('updateStaffMember: column error, retrying with core fields only:', error.message);
    const corePayload = {
      full_name: payload.full_name,
      email: payload.email,
      phone: payload.phone,
      position: payload.position,
      pin: payload.pin,
      status: payload.status,
      permissions: payload.permissions,
      updated_at: new Date().toISOString(),
    };
    const retry = await supabase
      .from('employees')
      .update(corePayload)
      .eq('id', staffId)
      .eq('restaurant_id', _rid())
      .select()
      .single();
    data = retry.data;
    error = retry.error;
  }
  
  if (error) console.error('updateStaffMember error:', error);
  return { data, error };
}

// DELETE staff member (soft delete - sets status to terminated)
export async function deleteStaffMember(staffId, hardDelete = false) {
  if (hardDelete) {
    const { error } = await supabase
      .from('employees')
      .delete()
      .eq('id', staffId)
      .eq('restaurant_id', _rid());
    return { error };
  }
  
  // Soft delete - keep records but mark as terminated
  const { error } = await supabase
    .from('employees')
    .update({ status: 'terminated', end_date: new Date().toISOString().split('T')[0] })
    .eq('id', staffId)
    .eq('restaurant_id', _rid());
  return { error };
}

// CLOCK IN
export async function employeeClockIn(staffId, branchId = 'main') {
  // Check if already clocked in
  const { data: existing } = await supabase
    .from('employee_time_clock')
    .select('id, clock_in')
    .eq('employee_id', staffId)
    .is('clock_out', null)
    .maybeSingle();
  
  if (existing) {
    return { error: { message: 'Already clocked in at ' + new Date(existing.clock_in).toLocaleTimeString() } };
  }
  
  const { data, error } = await supabase
    .from('employee_time_clock')
    .insert({
      restaurant_id: _rid(),
      employee_id: staffId,
      branch_id: branchId,
      clock_in: new Date().toISOString(),
    })
    .select()
    .single();
  
  return { data, error };
}

// CLOCK OUT
export async function employeeClockOut(staffId, breakMinutes = 0) {
  // Find open clock record
  const { data: open } = await supabase
    .from('employee_time_clock')
    .select('id, clock_in')
    .eq('employee_id', staffId)
    .is('clock_out', null)
    .maybeSingle();
  
  if (!open) {
    return { error: { message: 'Not currently clocked in' } };
  }
  
  // Calculate hours
  const clockIn = new Date(open.clock_in);
  const clockOut = new Date();
  const totalMs = clockOut - clockIn;
  const totalMinutes = Math.floor(totalMs / 60000) - (breakMinutes || 0);
  const totalHours = Math.max(0, totalMinutes / 60).toFixed(2);
  
  const { data, error } = await supabase
    .from('employee_time_clock')
    .update({
      clock_out: clockOut.toISOString(),
      break_minutes: breakMinutes || 0,
      total_hours: parseFloat(totalHours),
    })
    .eq('id', open.id)
    .select()
    .single();
  
  return { data, error };
}

// FETCH clock records (for time tracking)
export async function fetchEmployeeClockRecords(staffId, fromDate, toDate) {
  let q = supabase
    .from('employee_time_clock')
    .select('*, employees(full_name, position)')
    .eq('restaurant_id', _rid())
    .order('clock_in', { ascending: false });
  
  if (staffId) q = q.eq('employee_id', staffId);
  if (fromDate) q = q.gte('clock_in', fromDate);
  if (toDate) q = q.lte('clock_in', toDate);
  
  const { data, error } = await q;
  if (error) console.error('fetchClockRecords:', error);
  return data || [];
}

// FETCH currently clocked in staff
export async function fetchCurrentlyClocked() {
  const { data, error } = await supabase
    .from('employee_time_clock')
    .select('*, employees(full_name, position, photo_url)')
    .eq('restaurant_id', _rid())
    .is('clock_out', null);
  if (error) console.error('fetchCurrentlyClocked:', error);
  return data || [];
}

// FETCH schedules
export async function fetchStaffSchedules(fromDate, toDate, staffId) {
  let q = supabase
    .from('employee_schedules')
    .select('*, employees(full_name, position)')
    .eq('restaurant_id', _rid())
    .order('schedule_date');
  
  if (fromDate) q = q.gte('schedule_date', fromDate);
  if (toDate) q = q.lte('schedule_date', toDate);
  if (staffId) q = q.eq('employee_id', staffId);
  
  const { data } = await q;
  return data || [];
}

// SAVE schedule
export async function saveStaffSchedule(schedule) {
  const payload = {
    restaurant_id: _rid(),
    employee_id: schedule.employee_id,
    branch_id: schedule.branch_id || 'main',
    schedule_date: schedule.schedule_date,
    shift_start: schedule.shift_start,
    shift_end: schedule.shift_end,
    break_minutes: schedule.break_minutes || 30,
    position: schedule.position || null,
    notes: schedule.notes || null,
    status: schedule.status || 'scheduled',
  };
  
  if (schedule.id) {
    const { data, error } = await supabase
      .from('employee_schedules')
      .update(payload)
      .eq('id', schedule.id)
      .select()
      .single();
    return { data, error };
  }
  
  const { data, error } = await supabase
    .from('employee_schedules')
    .insert(payload)
    .select()
    .single();
  return { data, error };
}

// DELETE schedule
export async function deleteStaffSchedule(scheduleId) {
  const { error } = await supabase
    .from('employee_schedules')
    .delete()
    .eq('id', scheduleId)
    .eq('restaurant_id', _rid());
  return { error };
}

// CALCULATE PAYROLL for a period
export async function calculatePayroll(staffId, fromDate, toDate) {
  // Get staff member
  const staff = await fetchStaffMember(staffId);
  if (!staff) return { error: 'Staff not found' };
  
  // Get clock records for period
  const records = await fetchClockRecords(staffId, fromDate, toDate);
  
  // Sum hours
  const totalHours = records.reduce((sum, r) => sum + (parseFloat(r.total_hours) || 0), 0);
  
  let basePay = 0;
  if (staff.payment_type === 'hourly' && staff.hourly_rate) {
    basePay = totalHours * parseFloat(staff.hourly_rate);
  } else if (staff.payment_type === 'salary' && staff.monthly_salary) {
    // Pro-rata for the period
    const days = (new Date(toDate) - new Date(fromDate)) / (1000 * 60 * 60 * 24);
    basePay = (parseFloat(staff.monthly_salary) / 30) * days;
  }
  
  return {
    staff,
    totalHours: totalHours.toFixed(2),
    basePay: basePay.toFixed(2),
    records: records.length,
    period: { from: fromDate, to: toDate }
  };
}

// SAVE payroll record
export async function saveStaffPayroll(payroll) {
  const payload = {
    restaurant_id: _rid(),
    employee_id: payroll.employee_id,
    pay_period_start: payroll.pay_period_start,
    pay_period_end: payroll.pay_period_end,
    hours_worked: payroll.hours_worked || 0,
    hourly_rate: payroll.hourly_rate || null,
    base_salary: payroll.base_salary || 0,
    commission: payroll.commission || 0,
    bonus: payroll.bonus || 0,
    deductions: payroll.deductions || 0,
    net_pay: payroll.net_pay,
    payment_date: payroll.payment_date || null,
    payment_method: payroll.payment_method || 'bank',
    payment_reference: payroll.payment_reference || null,
    status: payroll.status || 'pending',
    notes: payroll.notes || null,
    paid_by: payroll.paid_by || null,
  };
  
  if (payroll.id) {
    const { data, error } = await supabase
      .from('employee_payroll')
      .update(payload)
      .eq('id', payroll.id)
      .select()
      .single();
    return { data, error };
  }
  
  const { data, error } = await supabase
    .from('employee_payroll')
    .insert(payload)
    .select()
    .single();
  return { data, error };
}

// FETCH payroll history
export async function fetchPayrollHistory(staffId, fromDate, toDate) {
  let q = supabase
    .from('employee_payroll')
    .select('*, employees(full_name, position)')
    .eq('restaurant_id', _rid())
    .order('pay_period_end', { ascending: false });
  
  if (staffId) q = q.eq('employee_id', staffId);
  if (fromDate) q = q.gte('pay_period_start', fromDate);
  if (toDate) q = q.lte('pay_period_end', toDate);
  
  const { data } = await q;
  return data || [];
}

// ===========================================================
// STAFF PIN LOGIN SYSTEM
// ===========================================================

// Verify PIN and get staff member
export async function verifyStaffPIN(pin) {
  const { data, error } = await supabase
    .from('employees')
    .select('*')
    .eq('restaurant_id', _rid())
    .eq('pin', pin)
    .eq('status', 'active')
    .maybeSingle();
  
  if (error) {
    console.error('verifyStaffPIN error:', error);
    return { error: 'PIN check failed' };
  }
  
  if (!data) {
    return { error: 'Invalid PIN' };
  }
  
  return { staff: data };
}

// Save active staff to localStorage
export function setActiveStaff(staff) {
  if (staff) {
    localStorage.setItem('latavola_active_staff', JSON.stringify({
      id: staff.id,
      full_name: staff.full_name,
      position: staff.position,
      permissions: staff.permissions,
      pin: staff.pin,
      employee_id: staff.employee_id,
    }));
  } else {
    localStorage.removeItem('latavola_active_staff');
  }
}

// Get currently active staff
export function getActiveStaff() {
  try {
    const data = localStorage.getItem('latavola_active_staff');
    return data ? JSON.parse(data) : null;
  } catch (e) {
    return null;
  }
}

// Clear active staff (sign them out)
export function clearActiveStaff() {
  localStorage.removeItem('latavola_active_staff');
}

// Check if current staff has a permission
export function staffHasPermission(permName) {
  const staff = getActiveStaff();
  if (!staff) return true; // No staff logged in = restaurant owner = full access
  if (!staff.permissions) return false;
  return staff.permissions[permName] === true;
}


// ===========================================================
// FEATURE FLAGS & SERVICE TYPES
// ===========================================================

// Plan-based features definition
export const PLAN_FEATURES = {
  starter: {
    pos: true,
    menu_management: true,
    basic_reports: true,
    receipts: true,
    stock_tracking: true,
    branches_limit: 1,
    staff_limit: 3,
    online_ordering: false,
    bookings: false,
    online_payments: false,
    promo_codes: false,
    set_meals: false,
    auto_offers: false,
    custom_url: false,
    advanced_analytics: false,
    inventory_advanced: false,
    cross_branch_reports: false,
    custom_domain: false,
    priority_support: false,
  },
  pro: {
    pos: true,
    menu_management: true,
    basic_reports: true,
    receipts: true,
    stock_tracking: true,
    branches_limit: 2,
    staff_limit: 10,
    online_ordering: true,
    bookings: true,
    online_payments: true,
    promo_codes: true,
    set_meals: true,
    auto_offers: true,
    custom_url: true,
    advanced_analytics: false,
    inventory_advanced: false,
    cross_branch_reports: false,
    custom_domain: false,
    priority_support: false,
  },
  enterprise: {
    pos: true,
    menu_management: true,
    basic_reports: true,
    receipts: true,
    stock_tracking: true,
    branches_limit: 999,
    staff_limit: 999,
    online_ordering: true,
    bookings: true,
    online_payments: true,
    promo_codes: true,
    set_meals: true,
    auto_offers: true,
    custom_url: true,
    advanced_analytics: true,
    inventory_advanced: true,
    cross_branch_reports: true,
    custom_domain: true,
    priority_support: true,
  }
};

// Check if restaurant has a feature (combines plan + addons + locks + service types)
export function hasFeature(restaurant, feature) {
  if (!restaurant) return false;
  
  // Check locks first (Super Admin overrides)
  const locks = restaurant.feature_locks || {};
  if (locks[feature] === 'lock_off') return false;
  if (locks[feature] === 'lock_on') return true;
  
  // Check addon features (highest priority among allowed)
  if (restaurant.addon_features?.[feature] === true) return true;
  
  // Check plan-based features
  const planFeatures = PLAN_FEATURES[restaurant.plan || 'starter'] || PLAN_FEATURES.starter;
  return planFeatures[feature] === true;
}

// Check if restaurant offers a service type
export function hasService(restaurant, serviceType) {
  if (!restaurant) return false;
  
  // Check locks first (Super Admin overrides)
  const locks = restaurant.feature_locks || {};
  if (locks[serviceType] === 'lock_off') return false;
  if (locks[serviceType] === 'lock_on') return true;
  
  // Default to true if not configured (legacy support)
  if (!restaurant.service_types) return true;
  return restaurant.service_types[serviceType] !== false;
}

// Check if a feature is locked (returns 'lock_on', 'lock_off', or 'allow')
export function getFeatureLockState(restaurant, feature) {
  if (!restaurant?.feature_locks) return 'allow';
  return restaurant.feature_locks[feature] || 'allow';
}

// Check if owner can toggle this feature
export function canOwnerToggle(restaurant, feature) {
  const lockState = getFeatureLockState(restaurant, feature);
  return lockState === 'allow';
}

// Get the limit for a feature (e.g., branches_limit, staff_limit)
export function getFeatureLimit(restaurant, feature) {
  if (!restaurant) return 0;
  const planFeatures = PLAN_FEATURES[restaurant.plan || 'starter'] || PLAN_FEATURES.starter;
  return planFeatures[feature] || 0;
}

// Update service types
export async function updateServiceTypes(restaurantId, serviceTypes) {
  const { data, error } = await supabase
    .from('restaurants')
    .update({ service_types: serviceTypes })
    .eq('id', restaurantId)
    .select()
    .single();
  
  if (error) console.error('updateServiceTypes:', error);
  return { data, error };
}

// Update addon features (super admin only)
export async function updateAddonFeatures(restaurantId, addonFeatures) {
  const { data, error } = await supabase
    .from('restaurants')
    .update({ addon_features: addonFeatures })
    .eq('id', restaurantId)
    .select()
    .single();
  
  if (error) console.error('updateAddonFeatures:', error);
  return { data, error };
}

// Update feature locks (super admin only)
export async function updateFeatureLocks(restaurantId, featureLocks) {
  const { data, error } = await supabase
    .from('restaurants')
    .update({ feature_locks: featureLocks })
    .eq('id', restaurantId)
    .select()
    .single();
  
  if (error) console.error('updateFeatureLocks:', error);
  return { data, error };
}

// ===========================================================
// SUBSCRIPTION STATUS CHECK
// ===========================================================

// Check if a restaurant's subscription is valid
// Returns: {valid: bool, reason: string|null, daysLeft: number|null, status: 'active'|'trial'|'expired'|'suspended'}
export function checkSubscriptionStatus(restaurant) {
  if (!restaurant) return { valid: false, reason: 'No restaurant', status: 'expired' };
  
  // Check if suspended
  if (restaurant.active === false) {
    return { valid: false, reason: 'Account suspended by admin', status: 'suspended' };
  }
  
  // Check trial expired
  if (restaurant.plan === 'trial' && restaurant.trial_ends_at) {
    const trialEnd = new Date(restaurant.trial_ends_at);
    const now = new Date();
    const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) {
      return { valid: false, reason: 'Free trial expired', status: 'expired', daysLeft: 0 };
    }
    return { valid: true, status: 'trial', daysLeft };
  }
  
  // Check paid subscription expired
  if (restaurant.subscription_ends_at) {
    const subEnd = new Date(restaurant.subscription_ends_at);
    const now = new Date();
    const daysLeft = Math.ceil((subEnd - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) {
      return { valid: false, reason: 'Subscription expired', status: 'expired', daysLeft: 0 };
    }
    return { valid: true, status: 'active', daysLeft };
  }
  
  // No expiry set = lifetime access (for friends/admin override)
  return { valid: true, status: 'active', daysLeft: null };
}

// ===========================================================
// IMAGE UPLOAD - For menu items
// ===========================================================

// Upload an image file to Supabase Storage
// Returns { url, error }
export async function uploadMenuImage(file, restaurantId) {
  if (!file) return { error: { message: 'No file provided' } };
  
  // Validate file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  if (!validTypes.includes(file.type)) {
    return { error: { message: 'Invalid file type. Please use JPG, PNG, WebP, or GIF.' } };
  }
  
  // Validate file size (5MB max)
  if (file.size > 5 * 1024 * 1024) {
    return { error: { message: 'File too large. Maximum 5MB.' } };
  }
  
  // Generate unique filename
  const ext = file.name.split('.').pop().toLowerCase();
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const filename = `${restaurantId || 'shared'}/${timestamp}-${random}.${ext}`;
  
  try {
    const { error } = await supabase.storage
      .from('menu-images')
      .upload(filename, file, {
        cacheControl: '3600',
        upsert: false
      });
    
    if (error) {
      console.error('Upload error:', error);
      return { error };
    }
    
    // Get public URL
    const { data: urlData } = supabase.storage
      .from('menu-images')
      .getPublicUrl(filename);
    
    return { url: urlData.publicUrl, path: filename };
  } catch (err) {
    console.error('Upload exception:', err);
    return { error: { message: err.message } };
  }
}

// Delete a menu image
export async function deleteMenuImage(imageUrl) {
  if (!imageUrl) return { success: true };
  
  try {
    // Extract path from URL
    const match = imageUrl.match(/menu-images\/(.+)$/);
    if (!match) return { success: false, error: { message: 'Invalid image URL' } };
    
    const path = match[1];
    const { error } = await supabase.storage
      .from('menu-images')
      .remove([path]);
    
    if (error) {
      console.error('Delete image error:', error);
      return { success: false, error };
    }
    
    return { success: true };
  } catch (err) {
    return { success: false, error: { message: err.message } };
  }
}

// ===========================================================
// SALES REP SYSTEM
// ===========================================================

// === SALES REP CRUD ===

export async function fetchAllReps() {
  const { data, error } = await supabase
    .from('sales_reps')
    .select('*')
    .order('full_name');
  if (error) console.error('fetchAllReps:', error);
  return data || [];
}

export async function fetchRepPerformance() {
  const { data, error } = await supabase
    .from('rep_performance')
    .select('*');
  if (error) console.error('fetchRepPerformance:', error);
  return data || [];
}

export async function createRep(rep) {
  const { data, error } = await supabase
    .from('sales_reps')
    .insert({
      email: rep.email.toLowerCase().trim(),
      password_hash: rep.password, // TODO: hash this
      full_name: rep.full_name,
      phone: rep.phone || null,
      commission_type: rep.commission_type || 'percentage',
      commission_percent: rep.commission_percent || 15,
      fixed_per_signup: rep.fixed_per_signup || 0,
      base_salary: rep.base_salary || 0,
      recurring_commission: rep.recurring_commission || false,
      notes: rep.notes || null,
    })
    .select()
    .single();
  if (error) console.error('createRep:', error);
  return { data, error };
}

export async function updateRep(repId, updates) {
  const { id, created_at, updated_at, ...cleanUpdates } = updates;
  const { data, error } = await supabase
    .from('sales_reps')
    .update({ ...cleanUpdates, updated_at: new Date().toISOString() })
    .eq('id', repId)
    .select()
    .single();
  if (error) console.error('updateRep:', error);
  return { data, error };
}

export async function deleteRep(repId) {
  const { error } = await supabase
    .from('sales_reps')
    .delete()
    .eq('id', repId);
  return { error };
}

// === REP LOGIN ===

export async function loginRep(email, password) {
  const { data: rep, error } = await supabase
    .from('sales_reps')
    .select('*')
    .eq('email', email.toLowerCase().trim())
    .eq('active', true)
    .maybeSingle();
  
  if (error || !rep) {
    return { error: { message: 'Invalid email or password' } };
  }
  
  if (rep.password_hash !== password) {
    return { error: { message: 'Invalid email or password' } };
  }
  
  // Update last login
  await supabase
    .from('sales_reps')
    .update({ last_login_at: new Date().toISOString() })
    .eq('id', rep.id);
  
  return { data: { ...rep, password_hash: undefined } };
}

// Save current rep to localStorage
export function saveCurrentRep(rep) {
  try {
    localStorage.setItem('latavola_current_rep', JSON.stringify(rep));
  } catch (e) {}
}

export function getCurrentRep() {
  try {
    const raw = localStorage.getItem('latavola_current_rep');
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return null;
}

export function logoutRep() {
  try {
    localStorage.removeItem('latavola_current_rep');
  } catch (e) {}
}

// === REP SUBSCRIPTIONS ===

export async function fetchRepSubscriptions(repId) {
  const { data, error } = await supabase
    .from('rep_subscriptions')
    .select('*, restaurants(name, plan, active, subscription_ends_at, trial_ends_at, created_at)')
    .eq('rep_id', repId)
    .order('signed_at', { ascending: false });
  if (error) console.error('fetchRepSubscriptions:', error);
  return data || [];
}

export async function assignRepToRestaurant(repId, restaurantId, signupCommission, monthlyCommission) {
  const { data, error } = await supabase
    .from('rep_subscriptions')
    .insert({
      rep_id: repId,
      restaurant_id: restaurantId,
      signup_commission: signupCommission || 0,
      monthly_commission: monthlyCommission || 0,
    })
    .select()
    .single();
  if (error) console.error('assignRepToRestaurant:', error);
  return { data, error };
}

export async function removeRepAssignment(subscriptionId) {
  const { error } = await supabase
    .from('rep_subscriptions')
    .delete()
    .eq('id', subscriptionId);
  return { error };
}

// === COMMISSIONS ===

export async function fetchRepCommissions(repId) {
  const { data, error } = await supabase
    .from('rep_commissions')
    .select('*, rep_subscriptions(restaurants(name))')
    .eq('rep_id', repId)
    .order('created_at', { ascending: false });
  if (error) console.error('fetchRepCommissions:', error);
  return data || [];
}

export async function fetchAllCommissions() {
  const { data, error } = await supabase
    .from('rep_commissions')
    .select('*, sales_reps(full_name, email), rep_subscriptions(restaurants(name))')
    .order('created_at', { ascending: false });
  if (error) console.error('fetchAllCommissions:', error);
  return data || [];
}

export async function createCommission(commission) {
  const { data, error } = await supabase
    .from('rep_commissions')
    .insert({
      rep_id: commission.rep_id,
      subscription_id: commission.subscription_id,
      amount: commission.amount,
      type: commission.type || 'signup',
      description: commission.description,
      period_start: commission.period_start,
      period_end: commission.period_end,
    })
    .select()
    .single();
  if (error) console.error('createCommission:', error);
  return { data, error };
}

export async function markCommissionPaid(commissionId, paymentMethod, paymentReference) {
  const { data, error } = await supabase
    .from('rep_commissions')
    .update({
      status: 'paid',
      paid_at: new Date().toISOString(),
      payment_method: paymentMethod,
      payment_reference: paymentReference,
    })
    .eq('id', commissionId)
    .select()
    .single();
  return { data, error };
}

// Calculate commission for a restaurant signup
export function calculateSignupCommission(rep, planPrice) {
  if (!rep || !planPrice) return 0;
  
  switch (rep.commission_type) {
    case 'percentage':
      return (planPrice * rep.commission_percent) / 100;
    case 'fixed':
      return rep.fixed_per_signup;
    case 'hybrid':
      return ((planPrice * rep.commission_percent) / 100) + rep.fixed_per_signup;
    case 'salary':
      return 0; // salary-only reps don't get per-signup commission
    default:
      return 0;
  }
}


// ===========================================================
// MARKETING SYSTEM - SMS & Email Credits + Campaigns
// ===========================================================

// === CREDIT PACKAGES (server-side truth) ===
export const SMS_PACKAGES = [
  { id: 'sms_starter', name: 'Starter', credits: 100, price: 10, perUnit: 0.10 },
  { id: 'sms_popular', name: 'Popular', credits: 500, price: 45, perUnit: 0.09, recommended: true },
  { id: 'sms_business', name: 'Business', credits: 1000, price: 85, perUnit: 0.085 },
  { id: 'sms_enterprise', name: 'Enterprise', credits: 5000, price: 400, perUnit: 0.08 },
];

export const EMAIL_PACKAGES = [
  { id: 'email_starter', name: 'Starter', credits: 1000, price: 5, perUnit: 0.005 },
  { id: 'email_popular', name: 'Popular', credits: 5000, price: 20, perUnit: 0.004, recommended: true },
  { id: 'email_business', name: 'Business', credits: 10000, price: 35, perUnit: 0.0035 },
  { id: 'email_enterprise', name: 'Enterprise', credits: 50000, price: 150, perUnit: 0.003 },
];

// === FETCH CREDITS ===
export async function fetchCredits() {
  const { data, error } = await supabase
    .from('marketing_credits')
    .select('*')
    .eq('restaurant_id', _rid())
    .maybeSingle();
  if (error) console.error('fetchCredits:', error);
  if (!data) {
    // Create initial credit record
    await supabase.from('marketing_credits').insert({
      restaurant_id: _rid(),
      sms_credits: 0,
      email_credits: 0,
    });
    return { sms_credits: 0, email_credits: 0 };
  }
  return data;
}

// === BUY CREDITS (no payment integration yet - records intent) ===
export async function buyCredits(packageId) {
  let pkg = SMS_PACKAGES.find(p => p.id === packageId) || EMAIL_PACKAGES.find(p => p.id === packageId);
  if (!pkg) return { error: { message: 'Invalid package' } };
  
  const type = packageId.startsWith('sms_') ? 'sms' : 'email';
  
  // Record purchase
  const { data: purchase, error: purchaseError } = await supabase
    .from('credit_purchases')
    .insert({
      restaurant_id: _rid(),
      type,
      amount: pkg.credits,
      price: pkg.price,
      payment_status: 'pending', // Will be 'completed' after Stripe payment
      package_name: pkg.name,
    })
    .select()
    .single();
  
  if (purchaseError) return { error: purchaseError };
  
  return { data: purchase, package: pkg };
}

// === COMPLETE PURCHASE (after payment confirmed) ===
export async function completePurchase(purchaseId, paymentMethod, paymentReference) {
  // Get the purchase
  const { data: purchase, error: pErr } = await supabase
    .from('credit_purchases')
    .select('*')
    .eq('id', purchaseId)
    .single();
  
  if (pErr || !purchase) return { error: pErr || { message: 'Purchase not found' } };
  
  // Update purchase status
  await supabase
    .from('credit_purchases')
    .update({
      payment_status: 'completed',
      payment_method: paymentMethod,
      payment_reference: paymentReference,
      completed_at: new Date().toISOString(),
    })
    .eq('id', purchaseId);
  
  // Add credits to restaurant
  const { data: currentCredits } = await supabase
    .from('marketing_credits')
    .select('*')
    .eq('restaurant_id', purchase.restaurant_id)
    .single();
  
  const updates = currentCredits ? {
    sms_credits: purchase.type === 'sms' ? currentCredits.sms_credits + purchase.amount : currentCredits.sms_credits,
    email_credits: purchase.type === 'email' ? currentCredits.email_credits + purchase.amount : currentCredits.email_credits,
    sms_purchased: purchase.type === 'sms' ? currentCredits.sms_purchased + purchase.amount : currentCredits.sms_purchased,
    email_purchased: purchase.type === 'email' ? currentCredits.email_purchased + purchase.amount : currentCredits.email_purchased,
    updated_at: new Date().toISOString(),
  } : {
    restaurant_id: purchase.restaurant_id,
    sms_credits: purchase.type === 'sms' ? purchase.amount : 0,
    email_credits: purchase.type === 'email' ? purchase.amount : 0,
    sms_purchased: purchase.type === 'sms' ? purchase.amount : 0,
    email_purchased: purchase.type === 'email' ? purchase.amount : 0,
  };
  
  if (currentCredits) {
    await supabase
      .from('marketing_credits')
      .update(updates)
      .eq('restaurant_id', purchase.restaurant_id);
  } else {
    await supabase
      .from('marketing_credits')
      .insert(updates);
  }
  
  return { success: true };
}

// === FETCH PURCHASES (history) ===
export async function fetchPurchases() {
  const { data, error } = await supabase
    .from('credit_purchases')
    .select('*')
    .eq('restaurant_id', _rid())
    .order('created_at', { ascending: false });
  if (error) console.error('fetchPurchases:', error);
  return data || [];
}

// === CAMPAIGNS ===
export async function fetchCampaigns() {
  const { data, error } = await supabase
    .from('marketing_campaigns')
    .select('*')
    .eq('restaurant_id', _rid())
    .order('created_at', { ascending: false });
  if (error) console.error('fetchCampaigns:', error);
  return data || [];
}

export async function fetchCampaign(campaignId) {
  const { data, error } = await supabase
    .from('marketing_campaigns')
    .select('*, campaign_recipients(*)')
    .eq('id', campaignId)
    .single();
  if (error) console.error('fetchCampaign:', error);
  return data;
}

export async function createCampaign(campaign) {
  const { data, error } = await supabase
    .from('marketing_campaigns')
    .insert({
      restaurant_id: _rid(),
      name: campaign.name,
      type: campaign.type,
      subject: campaign.subject || null,
      message: campaign.message,
      html_body: campaign.html_body || null,
      audience_type: campaign.audience_type || 'all',
      audience_filters: campaign.audience_filters || {},
      recipient_count: campaign.recipient_count || 0,
      estimated_cost: campaign.estimated_cost || 0,
      scheduled_at: campaign.scheduled_at || null,
      created_by: campaign.created_by || null,
    })
    .select()
    .single();
  if (error) console.error('createCampaign:', error);
  return { data, error };
}

export async function deleteCampaign(campaignId) {
  const { error } = await supabase
    .from('marketing_campaigns')
    .delete()
    .eq('id', campaignId);
  return { error };
}

// === SEND CAMPAIGN (simulated for now - no real Twilio/SendGrid yet) ===
export async function sendCampaign(campaignId, recipients) {
  // Get campaign
  const { data: campaign } = await supabase
    .from('marketing_campaigns')
    .select('*')
    .eq('id', campaignId)
    .single();
  
  if (!campaign) return { error: { message: 'Campaign not found' } };
  
  // Check credits
  const credits = await fetchCredits();
  const needed = recipients.length;
  const balance = campaign.type === 'sms' ? credits.sms_credits : credits.email_credits;
  
  if (balance < needed) {
    return { error: { message: `Not enough credits. Need ${needed}, have ${balance}` } };
  }
  
  // Update campaign status
  await supabase
    .from('marketing_campaigns')
    .update({
      status: 'sending',
      started_at: new Date().toISOString(),
      recipient_count: recipients.length,
    })
    .eq('id', campaignId);
  
  // Insert all recipients
  const recipientRecords = recipients.map(r => ({
    campaign_id: campaignId,
    customer_id: r.id || null,
    phone: r.phone || null,
    email: r.email || null,
    name: r.name || 'Customer',
    status: 'pending',
  }));
  
  await supabase.from('campaign_recipients').insert(recipientRecords);
  
  // SIMULATE SENDING (in production, this would call Twilio/SendGrid)
  // For now, mark all as sent after 1 second
  setTimeout(async () => {
    // Mark all as sent
    await supabase
      .from('campaign_recipients')
      .update({ status: 'sent', sent_at: new Date().toISOString() })
      .eq('campaign_id', campaignId);
    
    // Mark campaign as complete
    await supabase
      .from('marketing_campaigns')
      .update({
        status: 'sent',
        completed_at: new Date().toISOString(),
        sent_count: recipients.length,
        delivered_count: recipients.length,
        credits_used: recipients.length,
        estimated_cost: recipients.length * (campaign.type === 'sms' ? 0.10 : 0.005),
      })
      .eq('id', campaignId);
  }, 2000);
  
  // Deduct credits
  const newCredits = balance - needed;
  await supabase
    .from('marketing_credits')
    .update({
      [campaign.type === 'sms' ? 'sms_credits' : 'email_credits']: newCredits,
      [campaign.type === 'sms' ? 'sms_used' : 'email_used']: (campaign.type === 'sms' ? credits.sms_used : credits.email_used) + needed,
      updated_at: new Date().toISOString(),
    })
    .eq('restaurant_id', _rid());
  
  return { success: true, count: recipients.length };
}

// === OPT-OUTS ===
export async function fetchOptouts() {
  const { data, error } = await supabase
    .from('marketing_optouts')
    .select('*')
    .eq('restaurant_id', _rid())
    .order('created_at', { ascending: false });
  if (error) console.error('fetchOptouts:', error);
  return data || [];
}

export async function addOptout(phone, email, type, reason) {
  const { data, error } = await supabase
    .from('marketing_optouts')
    .insert({
      restaurant_id: _rid(),
      phone: phone || null,
      email: email || null,
      type: type || 'both',
      reason: reason || 'Customer requested',
    })
    .select()
    .single();
  return { data, error };
}

// === AUDIENCE BUILDING ===
// Get customers matching audience filters
export async function getAudienceCount(audienceType, filters) {
  let query = supabase
    .from('customers')
    .select('*', { count: 'exact', head: true })
    .eq('restaurant_id', _rid());
  
  switch (audienceType) {
    case 'recent':
      // Customers who ordered in last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      query = query.gte('last_order_date', thirtyDaysAgo.toISOString());
      break;
    case 'vip':
      query = query.gte('total_orders', 10);
      break;
    case 'birthday':
      // Would need a birthday column with proper indexing - placeholder
      break;
    case 'all':
    default:
      break;
  }
  
  const { count } = await query;
  return count || 0;
}

export async function getAudienceCustomers(audienceType, filters, type) {
  let query = supabase
    .from('customers')
    .select('*')
    .eq('restaurant_id', _rid());
  
  // Filter by required contact info
  if (type === 'sms') {
    query = query.not('phone', 'is', null);
  } else if (type === 'email') {
    query = query.not('email', 'is', null);
  }
  
  switch (audienceType) {
    case 'recent':
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      query = query.gte('last_order_date', thirtyDaysAgo.toISOString());
      break;
    case 'vip':
      query = query.gte('total_orders', 10);
      break;
    default:
      break;
  }
  
  const { data, error } = await query;
  if (error) console.error('getAudienceCustomers:', error);
  return data || [];
}


// ===========================================================
// PHASE 2: REAL PAYMENTS & SENDING via Edge Functions
// ===========================================================

// Helper to call edge functions
async function callEdgeFunction(name, body) {
  const supabaseUrl = supabase.supabaseUrl;
  const supabaseKey = supabase.supabaseKey;
  
  const response = await fetch(`${supabaseUrl}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${supabaseKey}`,
      'Content-Type': 'application/json',
      'apikey': supabaseKey,
    },
    body: JSON.stringify(body),
  });
  
  const data = await response.json();
  if (!response.ok) {
    return { error: { message: data.error || 'Edge function error' } };
  }
  return { data };
}

// === STRIPE CHECKOUT (Real Payment) ===
export async function createStripeCheckout(packageId) {
  const pkg = SMS_PACKAGES.find(p => p.id === packageId) || EMAIL_PACKAGES.find(p => p.id === packageId);
  if (!pkg) return { error: { message: 'Invalid package' } };
  
  const type = packageId.startsWith('sms_') ? 'sms' : 'email';
  const restaurantId = _rid();
  
  // Create pending purchase record
  const { data: purchase, error: purchaseError } = await supabase
    .from('credit_purchases')
    .insert({
      restaurant_id: restaurantId,
      type,
      amount: pkg.credits,
      price: pkg.price,
      payment_status: 'pending',
      payment_method: 'stripe',
      package_name: pkg.name,
    })
    .select()
    .single();
  
  if (purchaseError) return { error: purchaseError };
  
  // Call edge function to create Stripe Checkout session
  const successUrl = `${window.location.origin}/?payment=success&purchase=${purchase.id}`;
  const cancelUrl = `${window.location.origin}/?payment=cancelled&purchase=${purchase.id}`;
  
  const result = await callEdgeFunction('stripe-checkout', {
    package_id: packageId,
    restaurant_id: restaurantId,
    purchase_id: purchase.id,
    success_url: successUrl,
    cancel_url: cancelUrl,
  });
  
  if (result.error) {
    // Mark purchase as failed
    await supabase
      .from('credit_purchases')
      .update({ payment_status: 'failed' })
      .eq('id', purchase.id);
    return { error: result.error };
  }
  
  return { data: { url: result.data.url, purchase_id: purchase.id } };
}

// === MANUAL PAYMENT - For restaurants paying via bank transfer ===
export async function recordManualPayment(packageId, paymentReference) {
  const pkg = SMS_PACKAGES.find(p => p.id === packageId) || EMAIL_PACKAGES.find(p => p.id === packageId);
  if (!pkg) return { error: { message: 'Invalid package' } };
  
  const type = packageId.startsWith('sms_') ? 'sms' : 'email';
  
  // Create purchase record (pending - super admin must approve)
  const { data: purchase, error: purchaseError } = await supabase
    .from('credit_purchases')
    .insert({
      restaurant_id: _rid(),
      type,
      amount: pkg.credits,
      price: pkg.price,
      payment_status: 'pending',
      payment_method: 'manual',
      payment_reference: paymentReference,
      package_name: pkg.name,
    })
    .select()
    .single();
  
  if (purchaseError) return { error: purchaseError };
  return { data: purchase };
}

// Super admin approves a manual payment
export async function approveManualPayment(purchaseId) {
  return completePurchase(purchaseId, 'manual', 'admin-approved');
}

// === REAL SEND SMS via Edge Function ===
export async function sendSmsCampaignReal(campaignId, recipients) {
  const restaurantId = _rid();
  
  // First create the campaign recipients
  const recipientRecords = recipients.map(r => ({
    campaign_id: campaignId,
    customer_id: r.id || null,
    phone: r.phone || null,
    name: r.name || 'Customer',
    status: 'pending',
  }));
  
  await supabase.from('campaign_recipients').insert(recipientRecords);
  
  // Mark campaign as sending
  await supabase
    .from('marketing_campaigns')
    .update({
      status: 'sending',
      started_at: new Date().toISOString(),
      recipient_count: recipients.length,
    })
    .eq('id', campaignId);
  
  // Deduct credits NOW (will refund failed ones if needed)
  const { data: credits } = await supabase
    .from('marketing_credits')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .single();
  
  if (credits) {
    await supabase
      .from('marketing_credits')
      .update({
        sms_credits: credits.sms_credits - recipients.length,
        sms_used: credits.sms_used + recipients.length,
        updated_at: new Date().toISOString(),
      })
      .eq('restaurant_id', restaurantId);
  }
  
  // Call edge function to send SMS
  const result = await callEdgeFunction('send-sms', {
    campaign_id: campaignId,
    restaurant_id: restaurantId,
  });
  
  if (result.error) {
    // Refund credits on failure
    if (credits) {
      await supabase
        .from('marketing_credits')
        .update({
          sms_credits: credits.sms_credits,
          sms_used: credits.sms_used,
        })
        .eq('restaurant_id', restaurantId);
    }
    return { error: result.error };
  }
  
  return { data: result.data };
}

// === REAL SEND EMAIL via Edge Function ===
export async function sendEmailCampaignReal(campaignId, recipients) {
  const restaurantId = _rid();
  
  // First create the campaign recipients
  const recipientRecords = recipients.map(r => ({
    campaign_id: campaignId,
    customer_id: r.id || null,
    email: r.email || null,
    name: r.name || 'Customer',
    status: 'pending',
  }));
  
  await supabase.from('campaign_recipients').insert(recipientRecords);
  
  // Mark campaign as sending
  await supabase
    .from('marketing_campaigns')
    .update({
      status: 'sending',
      started_at: new Date().toISOString(),
      recipient_count: recipients.length,
    })
    .eq('id', campaignId);
  
  // Deduct credits
  const { data: credits } = await supabase
    .from('marketing_credits')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .single();
  
  if (credits) {
    await supabase
      .from('marketing_credits')
      .update({
        email_credits: credits.email_credits - recipients.length,
        email_used: credits.email_used + recipients.length,
        updated_at: new Date().toISOString(),
      })
      .eq('restaurant_id', restaurantId);
  }
  
  // Call edge function
  const result = await callEdgeFunction('send-email', {
    campaign_id: campaignId,
    restaurant_id: restaurantId,
  });
  
  if (result.error) {
    if (credits) {
      await supabase
        .from('marketing_credits')
        .update({
          email_credits: credits.email_credits,
          email_used: credits.email_used,
        })
        .eq('restaurant_id', restaurantId);
    }
    return { error: result.error };
  }
  
  return { data: result.data };
}

// === CHECK if Edge Functions are configured ===
export async function checkEdgeFunctionsConfigured() {
  try {
    // Try a simple ping to see if functions are available
    const result = await callEdgeFunction('stripe-checkout', { test: true });
    return !result.error || !result.error.message?.includes('not found');
  } catch (e) {
    return false;
  }
}


// === SUPER ADMIN: Fetch ALL pending purchases across all restaurants ===
export async function fetchAllPendingPurchases() {
  const { data, error } = await supabase
    .from('credit_purchases')
    .select('*, restaurants(name, phone, address)')
    .eq('payment_status', 'pending')
    .order('created_at', { ascending: false });
  if (error) console.error('fetchAllPendingPurchases:', error);
  return data || [];
}

// === SUPER ADMIN: Fetch all purchases (history) ===
export async function fetchAllPurchases(limit) {
  let query = supabase
    .from('credit_purchases')
    .select('*, restaurants(name)')
    .order('created_at', { ascending: false });
  if (limit) query = query.limit(limit);
  const { data, error } = await query;
  if (error) console.error('fetchAllPurchases:', error);
  return data || [];
}


// === SUPER ADMIN: Reject a manual payment ===
export async function rejectManualPayment(purchaseId, reason) {
  const { error } = await supabase
    .from('credit_purchases')
    .update({ 
      payment_status: 'failed',
      payment_reference: (reason || 'rejected'),
    })
    .eq('id', purchaseId);
  return { error };
}


// === DRIVER LOGIN - Login from anywhere (drivers can be at any restaurant) ===
export async function loginDriverByEmail(email, password) {
  // Find driver by email + PIN. Table is 'employees', PIN serves as password.
  const { data, error } = await supabase
    .from('employees')
    .select('*, restaurants(*)')
    .eq('email', email.toLowerCase().trim())
    .eq('pin', password)
    .eq('position', 'driver')
    .eq('status', 'active')
    .maybeSingle();
  
  if (error) {
    console.error('loginDriverByEmail error:', error);
    return { error: { message: 'Login error: ' + error.message } };
  }
  if (!data) {
    return { error: { message: 'Invalid email or PIN. Check your details or ask your manager.' } };
  }
  
  return { data };
}

export async function loginDriverByPin(pin, restaurantSlug) {
  // PIN alone is NOT unique across restaurants!
  // Must also know which restaurant.
  let query = supabase
    .from('employees')
    .select('*, restaurants(*)')
    .eq('pin', pin)
    .eq('position', 'driver')
    .eq('status', 'active');
  
  const { data, error } = await query;
  
  if (error) {
    console.error('loginDriverByPin error:', error);
    return { error: { message: 'Login error: ' + error.message } };
  }
  
  if (!data || data.length === 0) {
    return { error: { message: 'Invalid PIN. Ask your manager for your PIN.' } };
  }
  
  // If restaurant slug provided, filter by it
  if (restaurantSlug) {
    const match = data.find(d => d.restaurants?.slug === restaurantSlug);
    if (match) return { data: match };
    return { error: { message: 'PIN not found for this restaurant.' } };
  }
  
  // Multiple drivers with same PIN at different restaurants - ambiguous!
  if (data.length > 1) {
    return { 
      error: { 
        message: 'This PIN is used at multiple restaurants. Please use Email Login instead.',
        multiple: true,
      } 
    };
  }
  
  // Exactly one match - safe to login
  return { data: data[0] };
}

// Save current driver session
export function saveCurrentDriver(driver) {
  try {
    localStorage.setItem('latavola_current_driver', JSON.stringify(driver));
    if (driver?.restaurants) {
      setCurrentRestaurantId(driver.restaurants.id);
    }
  } catch (e) {}
}

export function getCurrentDriver() {
  try {
    const raw = localStorage.getItem('latavola_current_driver');
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return null;
}

export function logoutDriver() {
  try {
    localStorage.removeItem('latavola_current_driver');
  } catch (e) {}
}


// ===========================================================
// DRIVER CLAIM SYSTEM - Multiple drivers claim orders
// ===========================================================

// Fetch orders for driver view: available (unclaimed) + my claimed orders
export async function fetchDriverOrders(driverId) {
  const restaurantId = _rid();
  
  // Get all delivery orders that are ready or being delivered
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .eq('type', 'delivery')
    .in('status', ['ready', 'out_for_delivery', 'delivered'])
    .order('created_at', { ascending: false })
    .limit(100);
  
  if (error) {
    console.error('fetchDriverOrders:', error);
    return { available: [], mine: [], completed: [] };
  }
  
  const orders = data || [];
  
  // An order needs cash collection if it's delivered but still NOT paid
  // (regardless of whether pay_method is 'cod', 'cash', or null)
  const needsCash = (o) => o.status === 'delivered' && !o.paid;
  
  // Categorize
  const available = orders.filter(o => 
    o.status === 'ready' && !o.assigned_driver_id
  );
  
  const mine = orders.filter(o => 
    o.assigned_driver_id === driverId && 
    (o.status === 'ready' || o.status === 'out_for_delivery' || needsCash(o))
  );
  
  // Completed = delivered AND paid (cash collected, or was paid online)
  const completed = orders.filter(o => 
    o.assigned_driver_id === driverId && 
    o.status === 'delivered' && 
    o.paid
  );
  
  return { available, mine, completed };
}

// Driver claims an order - PROTECTED against double-claim
export async function claimOrder(orderId, driverId, driverName) {
  // Only claim if NOT already assigned (prevents two drivers claiming same)
  const { data, error } = await supabase
    .from('orders')
    .update({
      assigned_driver_id: driverId,
      assigned_driver_name: driverName,
      claimed_at: new Date().toISOString(),
    })
    .eq('id', orderId)
    .is('assigned_driver_id', null)  // CRITICAL: only if unclaimed
    .select();
  
  if (error) {
    console.error('claimOrder:', error);
    return { error: { message: 'Failed to claim order' } };
  }
  
  // If no rows updated, someone else claimed it first
  if (!data || data.length === 0) {
    return { error: { message: 'This order was just claimed by another driver' } };
  }
  
  return { data: data[0] };
}

// Driver releases an order back to the pool
export async function unclaimOrder(orderId, driverId) {
  const { data, error } = await supabase
    .from('orders')
    .update({
      assigned_driver_id: null,
      assigned_driver_name: null,
      claimed_at: null,
    })
    .eq('id', orderId)
    .eq('assigned_driver_id', driverId)  // Only unclaim own orders
    .eq('status', 'ready')  // Can only release if not yet out for delivery
    .select();
  
  if (error) return { error };
  if (!data || data.length === 0) {
    return { error: { message: 'Cannot release this order' } };
  }
  return { data: data[0] };
}

// Mark order as out for delivery (driver picked it up)
export async function startDelivery(orderId, driverId) {
  const { data, error } = await supabase
    .from('orders')
    .update({ status: 'out_for_delivery' })
    .eq('id', orderId)
    .eq('assigned_driver_id', driverId)
    .select();
  
  if (error) return { error };
  return { data: data?.[0] };
}


// === DRIVER: Record cash collection with driver attribution ===
export async function recordDriverCashCollection(orderId, amount, driverId, driverName) {
  // Update order: mark paid, record cash amount + which driver collected
  let result = await supabase
    .from('orders')
    .update({
      paid: true,
      pay_method: 'cash',
      cash_collected: amount,
      delivered_by: driverName,
      assigned_driver_id: driverId,
      assigned_driver_name: driverName,
    })
    .eq('id', orderId)
    .select();
  
  if (!result.data || result.data.length === 0) {
    result = await supabase
      .from('orders')
      .update({
        paid: true,
        pay_method: 'cash',
        cash_collected: amount,
        delivered_by: driverName,
        assigned_driver_id: driverId,
        assigned_driver_name: driverName,
      })
      .eq('order_number', orderId)
      .select();
  }
  
  if (result.error) console.error('recordDriverCashCollection error:', result.error);
  return result;
}

// === DRIVER: Mark order delivered with driver attribution ===
export async function markOrderDelivered(orderId, driverId, driverName) {
  let result = await supabase
    .from('orders')
    .update({
      status: 'delivered',
      delivered_at: new Date().toISOString(),
      delivered_by: driverName,
    })
    .eq('id', orderId)
    .select();
  
  if (!result.data || result.data.length === 0) {
    result = await supabase
      .from('orders')
      .update({
        status: 'delivered',
        delivered_at: new Date().toISOString(),
        delivered_by: driverName,
      })
      .eq('order_number', orderId)
      .select();
  }
  
  if (result.error) console.error('markOrderDelivered error:', result.error);
  return result;
}


// ===========================================================
// CASH HANDOVER SYSTEM - Two-step driver -> manager
// ===========================================================

// DRIVER: Get cash that needs handover (delivered COD orders, paid, not yet in a handover)
export async function getDriverCashToHandover(driverId) {
  const restaurantId = _rid();
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .eq('type', 'delivery')
    .eq('status', 'delivered')
    .eq('pay_method', 'cash')
    .eq('paid', true)
    .is('handover_id', null)
    .eq('assigned_driver_id', driverId);
  
  if (error) {
    console.error('getDriverCashToHandover:', error);
    return { orders: [], total: 0 };
  }
  
  const orders = data || [];
  const total = orders.reduce((s, o) => s + parseFloat(o.cash_collected || o.total || 0), 0);
  return { orders, total };
}

// DRIVER: Initiate a handover
export async function initiateHandover(driverId, driverName, amount, orderIds) {
  const restaurantId = _rid();
  
  // Create handover record
  const { data: handover, error } = await supabase
    .from('cash_handovers')
    .insert({
      restaurant_id: restaurantId,
      driver_id: driverId,
      driver_name: driverName,
      driver_declared_amount: amount,
      order_ids: orderIds,
      order_count: orderIds.length,
      status: 'pending',
    })
    .select()
    .single();
  
  if (error) {
    console.error('initiateHandover:', error);
    return { error };
  }
  
  // Link orders to this handover
  if (orderIds.length > 0) {
    await supabase
      .from('orders')
      .update({ handover_id: handover.id })
      .in('id', orderIds);
  }
  
  return { data: handover };
}

// DRIVER: Check if there's a pending handover
export async function getDriverPendingHandover(driverId) {
  const { data, error } = await supabase
    .from('cash_handovers')
    .select('*')
    .eq('driver_id', driverId)
    .eq('status', 'pending')
    .order('initiated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  
  if (error) {
    console.error('getDriverPendingHandover:', error);
    return null;
  }
  return data;
}

// DRIVER: Cancel a pending handover (before manager confirms)
export async function cancelHandover(handoverId, driverId) {
  // Unlink orders
  await supabase
    .from('orders')
    .update({ handover_id: null })
    .eq('handover_id', handoverId);
  
  // Mark cancelled
  const { error } = await supabase
    .from('cash_handovers')
    .update({ status: 'cancelled' })
    .eq('id', handoverId)
    .eq('driver_id', driverId)
    .eq('status', 'pending');
  
  return { error };
}

// DRIVER/MANAGER: Get handover history
export async function getHandoverHistory(filters) {
  let query = supabase
    .from('cash_handovers')
    .select('*')
    .order('initiated_at', { ascending: false })
    .limit(50);
  
  // If filtering by driver, that's specific enough (don't also need restaurant filter)
  if (filters?.driverId) {
    query = query.eq('driver_id', filters.driverId);
  } else {
    // No driver filter = manager view = filter by restaurant
    query = query.eq('restaurant_id', _rid());
  }
  if (filters?.status) {
    query = query.eq('status', filters.status);
  }
  
  const { data, error } = await query;
  if (error) {
    console.error('getHandoverHistory:', error);
    return [];
  }
  return data || [];
}

// MANAGER: Get all pending handovers (drivers waiting for confirmation)
export async function getPendingHandovers() {
  const { data, error } = await supabase
    .from('cash_handovers')
    .select('*')
    .eq('restaurant_id', _rid())
    .eq('status', 'pending')
    .order('initiated_at', { ascending: false });
  
  if (error) {
    console.error('getPendingHandovers:', error);
    return [];
  }
  return data || [];
}

// MANAGER: Confirm a handover with actual counted amount
export async function confirmHandover(handoverId, managerId, managerName, confirmedAmount, note) {
  // Get the handover to calculate discrepancy
  const { data: handover } = await supabase
    .from('cash_handovers')
    .select('driver_declared_amount')
    .eq('id', handoverId)
    .single();
  
  if (!handover) {
    return { error: { message: 'Handover not found' } };
  }
  
  const declared = parseFloat(handover.driver_declared_amount || 0);
  const confirmed = parseFloat(confirmedAmount || 0);
  const discrepancy = confirmed - declared;
  
  const { data, error } = await supabase
    .from('cash_handovers')
    .update({
      manager_confirmed_amount: confirmed,
      discrepancy: discrepancy,
      manager_id: managerId || null,
      manager_name: managerName,
      status: 'confirmed',
      confirmed_at: new Date().toISOString(),
      note: note || null,
    })
    .eq('id', handoverId)
    .select()
    .single();
  
  if (error) {
    console.error('confirmHandover:', error);
    return { error };
  }
  
  return { data };
}


// ===========================================================
// TEAM CHAT - Internal staff messaging
// ===========================================================

export async function sendTeamMessage(senderName, senderRole, message) {
  const { data, error } = await supabase
    .from('team_messages')
    .insert({
      restaurant_id: _rid(),
      sender_name: senderName,
      sender_role: senderRole || 'staff',
      message: message,
    })
    .select()
    .single();
  if (error) console.error('sendTeamMessage:', error);
  return { data, error };
}

export async function fetchTeamMessages() {
  const { data, error } = await supabase
    .from('team_messages')
    .select('*')
    .eq('restaurant_id', _rid())
    .order('created_at', { ascending: true })
    .limit(100);
  if (error) {
    console.error('fetchTeamMessages:', error);
    return [];
  }
  return data || [];
}


// ===========================================================
// COMMISSION SYSTEM - Hybrid pricing
// ===========================================================

// Record commission when an online order is placed (commission-plan restaurants only)
export async function recordCommission(order, restaurant) {
  // Only for commission-billing restaurants with a rate set
  if (!restaurant || restaurant.billing_type !== 'commission') return null;
  const rate = parseFloat(restaurant.commission_rate || 0);
  if (rate <= 0) return null;
  
  const orderTotal = parseFloat(order.total || 0);
  const commissionAmount = orderTotal * (rate / 100);
  if (commissionAmount <= 0) return null;
  
  // Check if commission already exists for this order (prevent duplicates)
  if (order.dbId || order.id) {
    const orderIdToCheck = order.dbId;
    const orderNumberToCheck = order.id || order.order_number;
    const { data: existing } = await supabase
      .from('commission_ledger')
      .select('id, status')
      .or(`order_id.eq.${orderIdToCheck},order_number.eq.${orderNumberToCheck}`)
      .limit(1);
    if (existing && existing.length > 0) {
      // Already has a commission entry - don't duplicate
      console.log('Commission already exists for order:', orderNumberToCheck);
      return existing[0];
    }
  }
  
  const { data, error } = await supabase
    .from('commission_ledger')
    .insert({
      restaurant_id: restaurant.id,
      restaurant_name: restaurant.name,
      order_id: order.dbId || null,
      order_number: order.id || order.order_number || null,
      order_total: orderTotal,
      commission_rate: rate,
      commission_amount: commissionAmount,
      status: 'unpaid',
    })
    .select()
    .single();
  
  if (error) console.error('recordCommission:', error);
  return data;
}

// Reverse a commission entry (when order is cancelled/refunded)
// Marks it as 'reversed' so it doesn't count in monthly billing
export async function reverseCommission(orderId, orderNumber) {
  if (!orderId && !orderNumber) return null;
  const filter = orderId 
    ? `order_id.eq.${orderId}` 
    : `order_number.eq.${orderNumber}`;
  const { data, error } = await supabase
    .from('commission_ledger')
    .update({ status: 'reversed' })
    .or(filter)
    .eq('status', 'unpaid')  // only reverse if not yet paid
    .select();
  if (error) console.error('reverseCommission:', error);
  return data;
}

// Get commission summary for one restaurant
export async function getRestaurantCommission(restaurantId) {
  const { data, error } = await supabase
    .from('commission_ledger')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('getRestaurantCommission:', error);
    return { unpaid: 0, paid: 0, entries: [] };
  }
  
  const entries = data || [];
  const unpaid = entries.filter(e => e.status === 'unpaid')
    .reduce((s, e) => s + parseFloat(e.commission_amount || 0), 0);
  const paid = entries.filter(e => e.status === 'paid')
    .reduce((s, e) => s + parseFloat(e.commission_amount || 0), 0);
  const reversed = entries.filter(e => e.status === 'reversed')
    .reduce((s, e) => s + parseFloat(e.commission_amount || 0), 0);
  
  return { unpaid, paid, reversed, entries };
}

// SUPER ADMIN: Get commission owed by all restaurants
export async function getAllCommissions() {
  const { data, error } = await supabase
    .from('commission_ledger')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('getAllCommissions:', error);
    return [];
  }
  
  // Group by restaurant
  const byRestaurant = {};
  (data || []).forEach(e => {
    const key = e.restaurant_id;
    if (!byRestaurant[key]) {
      byRestaurant[key] = {
        restaurant_id: e.restaurant_id,
        restaurant_name: e.restaurant_name,
        unpaid: 0,
        paid: 0,
        reversed: 0,
        orderCount: 0,
        entries: [],
      };
    }
    const r = byRestaurant[key];
    r.entries.push(e);
    r.orderCount++;
    if (e.status === 'unpaid') r.unpaid += parseFloat(e.commission_amount || 0);
    else if (e.status === 'paid') r.paid += parseFloat(e.commission_amount || 0);
    else if (e.status === 'reversed') r.reversed += parseFloat(e.commission_amount || 0);
  });
  
  return Object.values(byRestaurant);
}

// SUPER ADMIN: Mark a restaurant's unpaid commission as paid
export async function markRestaurantCommissionPaid(restaurantId) {
  const { data, error } = await supabase
    .from('commission_ledger')
    .update({ status: 'paid', paid_at: new Date().toISOString() })
    .eq('restaurant_id', restaurantId)
    .eq('status', 'unpaid')
    .select();
  
  if (error) {
    console.error('markRestaurantCommissionPaid:', error);
    return { error };
  }
  // data is the array of rows that were updated
  const count = (data || []).length;
  if (count === 0) {
    return { error: { message: 'No unpaid commission found to mark as paid.' } };
  }
  return { success: true, count };
}


// ===========================================================
// RESTAURANT LOGO
// ===========================================================

// Save (or remove) a restaurant's logo URL
export async function saveRestaurantLogo(restaurantId, logoUrl) {
  const { data, error } = await supabase
    .from('restaurants')
    .update({ logo_url: logoUrl || null })
    .eq('id', restaurantId)
    .select()
    .single();
  if (error) console.error('saveRestaurantLogo:', error);
  return { data, error };
}

// Save a restaurant's logo display size
export async function saveRestaurantLogoSize(restaurantId, size) {
  const { data, error } = await supabase
    .from('restaurants')
    .update({ logo_size: size || 'medium' })
    .eq('id', restaurantId)
    .select()
    .single();
  if (error) console.error('saveRestaurantLogoSize:', error);
  return { data, error };
}

